import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import DetalleViajeSkeleton from "./DetalleViajeSkeleton";
import AnalisisDemorasModal from "./AnalisisDemorasModal";
import { useAuth } from "../context/AuthContext";
import {
  Package,
  Truck,
  MapPin,
  Clock,
  AlertCircle,
  CheckCircle,
  Play,
  Navigation,
  Filter,
  Search,
  RefreshCw,
  MessageCircle,
  AlertTriangle,
  RotateCcw,
  ArrowLeft,
  Building2,
  User,
  Calendar,
  Target,
  Flag,
  FileText,
  Info,
} from "lucide-react";

import { motion, useSpring, useTransform } from "framer-motion";

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const rad = (Math.PI / 180) * angleDeg;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
};

const describeArc = (cx, cy, r, startAngle, endAngle) => {
  const start = polarToCartesian(cx, cy, r, startAngle);
  const end = polarToCartesian(cx, cy, r, endAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
};

// Mapea porcentaje (0–100) → ángulo (-180 a 0)
const pctToAngle = (p) => -180 + (p * 180) / 100;

const VelocimetroViajes = ({ completados, total }) => {
  const pct = total > 0 ? (completados / total) * 100 : 0;

  // SVG base
  const width = 360;
  const height = 200;
  const cx = width / 2;
  const cy = height - 10;
  const r = 150;
  const stroke = 24;

  // Ángulo de la aguja (de -180 a 0)
  const angle = pctToAngle(Math.max(0, Math.min(100, pct)));
  const needleLen = r - 25;
  const needleTip = polarToCartesian(cx, cy, needleLen, angle);

  // Colores
  const rojo = "#ef4444";
  const amarillo = "#eab308";
  const verde = "#22c55e";
  const grisBase = "#e5e7eb";
  const eje = "#374151";
  const needleColor = pct >= 90 ? verde : pct >= 60 ? amarillo : rojo;

  // Ticks
  const ticks = [0, 20, 40, 60, 80, 90, 100];

  // Segmentos (ahora cubren 180° exactos)
  const aRojoIni = pctToAngle(0);
  const aRojoFin = pctToAngle(60);
  const aAmarIni = pctToAngle(60);
  const aAmarFin = pctToAngle(90);
  const aVerdeIni = pctToAngle(90);
  const aVerdeFin = pctToAngle(100);

  return (
    <div className="flex flex-col items-center">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Arco base gris */}
        <path
          d={describeArc(cx, cy, r, -180, 0)}
          fill="none"
          stroke={grisBase}
          strokeWidth={stroke}
          strokeLinecap="round"
        />

        {/* Rojo (0–60%) */}
        <path
          d={describeArc(cx, cy, r, aRojoIni, aRojoFin)}
          fill="none"
          stroke={rojo}
          strokeWidth={stroke}
          strokeLinecap="round"
        />

        {/* Amarillo (60–90%) */}
        <path
          d={describeArc(cx, cy, r, aAmarIni, aAmarFin)}
          fill="none"
          stroke={amarillo}
          strokeWidth={stroke}
          strokeLinecap="round"
        />

        {/* Verde (90–100%) */}
        <path
          d={describeArc(cx, cy, r, aVerdeIni, aVerdeFin)}
          fill="none"
          stroke={verde}
          strokeWidth={stroke}
          strokeLinecap="round"
        />

        {/* Aguja */}
        <line
          x1={cx}
          y1={cy}
          x2={needleTip.x}
          y2={needleTip.y}
          stroke={needleColor}
          strokeWidth="5"
          strokeLinecap="round"
        />

        {/* Eje */}
        <circle cx={cx} cy={cy} r="10" fill={eje} />
        <circle cx={cx} cy={cy} r="6" fill="white" />

        {/* Etiquetas fuera del arco */}
        {ticks.map((t) => {
          const a = pctToAngle(t);
          const pos = polarToCartesian(cx, cy, r + 20, a);
          return (
            <text
              key={t}
              x={pos.x}
              y={pos.y}
              fontSize="9"
              fontWeight="700"
              fill="#111827"
              textAnchor="middle"
              alignmentBaseline="middle"
            >
              {t}%
            </text>
          );
        })}

        {/* Porcentaje central */}
        <text
          x={cx}
          y={cy - 50}
          textAnchor="middle"
          fontSize="26"
          fontWeight="800"
          fill={needleColor}
        >
          {Math.round(pct)}%
        </text>
      </svg>

      {/* Texto inferior */}
      <div className="text-center mt-1">
        <p className="text-base font-bold text-gray-900">
          {completados} <span className="text-gray-500">/</span> {total}
        </p>
        <p className="text-xs text-gray-500 mt-1">Completados / Registrados</p>
      </div>
    </div>
  );
};

// Agregar después de tus imports y antes del componente
const calcularDistancia = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const calcularETA = (trackingData, destinoLat, destinoLon) => {
  if (!trackingData || trackingData.length < 2) return null;
  const ultimaPosicion = trackingData[trackingData.length - 1];
  const distanciaRestante = calcularDistancia(
    ultimaPosicion.latitude,
    ultimaPosicion.longitude,
    destinoLat,
    destinoLon,
  );
  const puntosRecientes = trackingData.slice(-15);
  const velocidadesValidas = puntosRecientes
    .map((p) => parseFloat(p.speed))
    .filter((v) => v > 0 && !isNaN(v));
  if (velocidadesValidas.length === 0) return null;
  const velocidadPromedio =
    velocidadesValidas.reduce((a, b) => a + b, 0) / velocidadesValidas.length;
  const horasRestantes = distanciaRestante / velocidadPromedio;
  const ahora = new Date();
  const eta = new Date(ahora.getTime() + horasRestantes * 60 * 60 * 1000);
  return eta.toLocaleTimeString("es-PE", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "America/Lima",
  });
};

const calcularAvance = (
  trackingData,
  origenLat,
  origenLon,
  destinoLat,
  destinoLon,
) => {
  if (!trackingData || trackingData.length === 0) return 0;
  const ultimaPosicion = trackingData[trackingData.length - 1];
  const distanciaTotal = calcularDistancia(
    origenLat,
    origenLon,
    destinoLat,
    destinoLon,
  );
  const distanciaRecorrida = calcularDistancia(
    origenLat,
    origenLon,
    ultimaPosicion.latitude,
    ultimaPosicion.longitude,
  );
  return Math.min(1, Math.max(0, distanciaRecorrida / distanciaTotal));
};
const SEMAFORO_CONFIG = {
  VERDE: {
    bg: "bg-gradient-to-r from-green-50 to-emerald-50",
    border: "border-green-300",
    text: "text-green-700",
    bar: "bg-green-500",
    label: "Óptimo",
  },
  AMARILLO: {
    bg: "bg-gradient-to-r from-yellow-50 to-amber-50",
    border: "border-yellow-300",
    text: "text-yellow-700",
    bar: "bg-yellow-500",
    label: "Retraso Leve",
  },
  ROJO: {
    bg: "bg-gradient-to-r from-red-50 to-rose-50",
    border: "border-red-300",
    text: "text-red-700",
    bar: "bg-red-600",
    label: "Retraso Crítico",
  },
  GRIS: {
    bg: "bg-gray-50",
    border: "border-gray-200",
    text: "text-gray-500",
    bar: "bg-gray-400",
    label: "Sin Datos",
  },
};
const SeguimientoViajes = ({ onVolverAtras }) => {
  const { user } = useAuth();
  const [viajes, setViajes] = useState([]);
  const [selectedViaje, setSelectedViaje] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [trackingData, setTrackingData] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState("all");
  const [filtroCliente, setFiltroCliente] = useState("all");
  const [filtroTransportista, setFiltroTransportista] = useState("all");
  const [tramosEnriquecidos, setTramosEnriquecidos] = useState([]);

  const [tramoAnalisis, setTramoAnalisis] = useState(null);
  const [filtroOperador, setFiltroOperador] = useState("all");
  const API_BASE_URL = process.env.REACT_APP_VILOX_FLOTA_API;
  const LARAVEL_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  const getApiHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const [fechaInicio, setFechaInicio] = useState(todayStr);
  const [fechaFin, setFechaFin] = useState(todayStr);
const simDemoInicializandoRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [loadingTracking, setLoadingTracking] = useState(false);

  const mapContainerRef = useRef(null);
  const isInitialized = useRef(false);
  const [toasts, setToasts] = useState([]);

  const [demoActivo, setDemoActivo] = useState(false);
  const [demoSession, setDemoSession] = useState(null);
  const [demoOrigen, setDemoOrigen] = useState(null);
  const [demoDestino, setDemoDestino] = useState(null);
  const demoPollingRef = useRef(null);
  const demoActivoRef = useRef(false);
  const demoStartTimeRef = useRef(null);
  const mapVersionRef = useRef(0);

  const markerAnimRef = useRef(null);
  const [simLoading, setSimLoading] = useState(false);
  const [simAlertasLoading, setSimAlertasLoading] = useState(false);
  const [simDemoIntervalo, setSimDemoIntervalo] = useState(30); // segundos entre tracks
  const [simDemoActivo, setSimDemoActivo] = useState(false);
  const simDemoActivoRef = useRef(false);
  const simDemoViajeIdRef = useRef(null);
  const simDemoSessionIdRef = useRef(null);
  const selectedViajeIdRef = useRef(null);
  const demoTracksAccRef = useRef([]);   // acumula tracks demo — nunca pierde histórico
  const simDemoPollRef = useRef(null);


  const showToast = (message, type = "success") => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type };
    setToasts((prev) => [...prev, newToast]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const loadGoogleMapsAPI = () => {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=drawing,places,geometry&v=3.64`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        if (window.google && window.google.maps) {
          resolve();
        } else {
          reject(new Error("Google Maps API failed to load"));
        }
      };

      script.onerror = () =>
        reject(new Error("Google Maps API script failed to load"));
      document.head.appendChild(script);
    });
  };

  const initializeMap = () => {
    if (!mapContainerRef.current || !window.google) {
      console.error("Map container or Google Maps API not available");
      return;
    }

    try {
      const peruCenter = { lat: -12.0464, lng: -77.0428 };

      window.trackingMap = new window.google.maps.Map(mapContainerRef.current, {
        zoom: 8,
        center: peruCenter,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "water",
            elementType: "geometry",
            stylers: [{ color: "#e3f2fd" }],
          },
          {
            featureType: "landscape",
            elementType: "geometry",
            stylers: [{ color: "#f5f5f5" }],
          },
        ],
      });

      window.trackingPolyline = null;
      window.trackingMarkers = [];

      console.log("🎉 Google Maps inicializado para tracking");
      showToast("Mapa de seguimiento inicializado");
    } catch (error) {
      console.error("Error initializing Google Maps:", error);
      showToast("Error al inicializar Google Maps", "error");
    }
  };

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const initializeEverything = async () => {
      try {
        await loadGoogleMapsAPI();
        initializeMap();
        await fetchViajes();
      } catch (error) {
        console.error("Error initializing:", error);
        showToast("Error al inicializar el mapa", "error");
      }
    };

    initializeEverything();
  }, []);

  // Restaurar demo al seleccionar viaje — funciona en cualquier máquina/browser
  useEffect(() => {
    if (!selectedViaje) return;
    if (simDemoPollRef.current) return; // ya hay polling activo
    const checkDemo = async () => {
      try {
        // Usamos el sessionId del localStorage para consultar el estado en Laravel
        const storedCheck = JSON.parse(localStorage.getItem(`vilox_sim_${selectedViaje.id}`) || "null");
        if (!storedCheck?.sessionId) return;
        const res = await fetch(`${API_BASE_URL}/api/demo/status/${storedCheck.sessionId}`, { headers: getApiHeaders() });
        if (!res.ok) return;
        const status = await res.json();
        if (!status.active) return;
        const stored = JSON.parse(localStorage.getItem(`vilox_sim_${selectedViaje.id}`) || "null");
        const iv = stored?.intervalSeconds || 30;
        simDemoSessionIdRef.current = storedCheck.sessionId;
        setSimDemoIntervalo(iv);
        setSimDemoActivo(true);
        simDemoActivoRef.current = true;
        simDemoViajeIdRef.current = selectedViaje.id;
        const demoViajeIdRestore = selectedViaje.id;
        // Forzar re-init de markers al restaurar (igual que al iniciar por primera vez)
        simDemoInicializandoRef.current = true;
        // Dibujar markers inmediatamente sin esperar el primer tick del interval
        await fetchViajeDetalle(demoViajeIdRestore, true, true);
        actualizarPosicionDemo(demoViajeIdRestore);
        simDemoPollRef.current = setInterval(() => {
          if (selectedViajeIdRef.current === demoViajeIdRestore) {
            fetchViajeDetalle(demoViajeIdRestore, true, true, true);
            actualizarPosicionDemo(demoViajeIdRestore);
          }
        }, iv * 1000);
        showToast("Demo restaurada — scheduler activo en servidor", "success");
      } catch (_) {}
    };
    checkDemo();
  }, [selectedViaje?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mantener ref sincronizado con el viaje seleccionado (sin stale closure en callbacks)
  useEffect(() => {
    selectedViajeIdRef.current = selectedViaje?.id || null;
  }, [selectedViaje?.id]);

  const hasGPSData = (trackingData) => {
    return trackingData && trackingData.length > 0;
  };

  const clearMap = () => {
    console.log("🧹 clearMap ejecutado — polyline:", window.trackingPolyline, "markers:", window.trackingMarkers?.length);
    if (!window.google || !window.trackingMap) return;

    if (window.trackingPolyline) {
      window.trackingPolyline.setMap(null);
      window.trackingPolyline = null;
    }

    if (window.trackingMarkers) {
      window.trackingMarkers.forEach((marker) => marker.setMap(null));
      window.trackingMarkers = [];
    }

    if (window.truckMarker) {
      window.truckMarker.setMap(null);
      window.truckMarker = null;
    }

    console.log("🧹 Mapa limpiado completamente");
  };

  // Actualización quirúrgica para modo Demo: no destruye markers existentes
  const updateDemoTrack = (tracks, viaje) => {
    if (!window.google || !window.trackingMap) return;

    const puntos = (tracks || [])
      .map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude), gpstime: p.gpstime }))
      .filter((p) => !isNaN(p.lat) && !isNaN(p.lng))
      .sort((a, b) => a.gpstime - b.gpstime);

    if (!puntos.length) return;

    const nuevaPos = puntos[puntos.length - 1];

    if (window.trackingPolyline) {
      // Actualización quirúrgica: solo path + animación del camión
      window.trackingPolyline.setPath(puntos);
      if (window.truckMarker) {
        const posActual = window.truckMarker.getPosition();
        animateMarker(
          window.truckMarker,
          { lat: posActual.lat(), lng: posActual.lng() },
          { lat: nuevaPos.lat, lng: nuevaPos.lng },
          1800
        );
      }
      return;
    }

    // Primera vez con tracks: crear polyline y mover camión a posición GPS real
    // (no llama a updateMapWithTracking para evitar clearMap + validaciones complejas)
    window.trackingPolyline = new window.google.maps.Polyline({
      path: puntos,
      geodesic: true,
      strokeColor: "#2563eb",
      strokeOpacity: 0.8,
      strokeWeight: 4,
      map: window.trackingMap,
    });

    if (window.truckMarker) {
      // Animar camión desde origen hasta primera posición GPS real
      const posActual = window.truckMarker.getPosition();
      animateMarker(
        window.truckMarker,
        { lat: posActual.lat(), lng: posActual.lng() },
        { lat: nuevaPos.lat, lng: nuevaPos.lng },
        2000
      );
    } else {
      // Edge case: camión no existe — crearlo directamente en la posición GPS
      const truckMarker = new window.google.maps.Marker({
        position: { lat: nuevaPos.lat, lng: nuevaPos.lng },
        map: window.trackingMap,
        title: `VEHÍCULO: ${viaje?.vehiculo?.placa || 'S/N'}`,
        icon: {
          path: "M20,8h-3V4H3C1.89,4 1,4.89 1,6v11h2c0,1.66 1.34,3 3,3s3,-1.34 3,-3h6c0,1.66 1.34,3 3,3s3,-1.34 3,-3h2V13L20,8zM6,18.5c-0.83,0 -1.5,-0.67 -1.5,-1.5s0.67,-1.5 1.5,-1.5s1.5,0.67 1.5,1.5S6.83,18.5 6,18.5zM18,18.5c-0.83,0 -1.5,-0.67 -1.5,-1.5s0.67,-1.5 1.5,-1.5s1.5,0.67 1.5,1.5S18.83,18.5 18,18.5zM17,5v3h2.5l1.96,2.5H17V9H9V7h8V5z",
          fillColor: '#2563eb', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2, scale: 1.2,
          anchor: new window.google.maps.Point(12, 12),
        },
      });
      window.truckMarker = truckMarker;
      if (!window.trackingMarkers) window.trackingMarkers = [];
      window.trackingMarkers.push(truckMarker);
    }
  };

const actualizarPosicionDemo = async (viajeId) => {
  if (!window.google || !window.trackingMap) return;
  try {
    const stored = JSON.parse(localStorage.getItem(`vilox_sim_${viajeId}`) || "null");
    const vehiculoId = stored?.vehiculoId || selectedViaje?.vehiculo?.id;
    if (!vehiculoId) { console.warn("🚛 actualizarPosicionDemo: sin vehiculoId"); return; }

    // Usar fecha local Peru (no UTC — evita desfase nocturno)
    const ahora = new Date();
    const hoy = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(ahora.getDate()).padStart(2, '0')}`;

    const tracks = await fetch(
      `${API_BASE_URL}/api/tracks-by-vehicle?vehiculoId=${vehiculoId}&inicio=${hoy}T00:00:00&fin=${hoy}T23:59:59`,
      { headers: getApiHeaders() }
    ).then(r => r.json());

    console.log(`🚛 actualizarPosicionDemo: ${tracks?.length ?? 0} tracks del día`);
    if (!tracks?.length) return;

    // Filtrar por sessionStartTime para excluir tracks de demos anteriores
    const sessionStartTime = stored?.sessionStartTime;
    const tracksDeEstaDemo = sessionStartTime
      ? tracks.filter(t => t.gpstime >= sessionStartTime)
      : tracks;

    console.log(`🚛 actualizarPosicionDemo: ${tracksDeEstaDemo.length} tracks de esta sesión (sessionStartTime=${sessionStartTime})`);
    if (!tracksDeEstaDemo.length) return;

    // Acumular solo tracks de esta demo
    const existingIds = new Set(demoTracksAccRef.current.map(t => t.id));
    const nuevos = tracksDeEstaDemo.filter(t => !existingIds.has(t.id));
    if (nuevos.length) {
      demoTracksAccRef.current = [...demoTracksAccRef.current, ...nuevos];
    }

    const allTracks = demoTracksAccRef.current;
    console.log(`🚛 actualizarPosicionDemo: ${allTracks.length} tracks acumulados, polyline=${!!window.trackingPolyline}, truck=${!!window.truckMarker}`);
    setTrackingData(allTracks);
    const viajeRes = await fetch(`${API_BASE_URL}/api/viajes/${viajeId}`,
      { headers: getApiHeaders() }).then(r => r.json());
    if (viajeRes) updateDemoTrack(allTracks, viajeRes);
  } catch (e) { console.error("❌ actualizarPosicionDemo error:", e); }
};

  const animateMarker = (marker, from, to, durationMs) => {
    if (markerAnimRef.current) cancelAnimationFrame(markerAnimRef.current);
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / durationMs, 1);
      marker.setPosition({ lat: from.lat + (to.lat - from.lat) * t, lng: from.lng + (to.lng - from.lng) * t });
      if (t < 1) markerAnimRef.current = requestAnimationFrame(step);
    };
    markerAnimRef.current = requestAnimationFrame(step);
  };

  const updateMapWithTracking = (tracking, viaje) => {
    if (!window.google || !window.trackingMap) return;

    try {
      const bounds = new window.google.maps.LatLngBounds();

      // 1. OBTENER TRAMOS Y VALIDAR EXISTENCIA DE ESTABLECIMIENTOS
      const tramos = viaje?.tramos || [];
      const primerTramo = tramos[0];
      const ultimoTramo = tramos[tramos.length - 1];

      // ⭐ DEBUG - Ver qué llega del backend
      console.group("🗺️ DEBUG MAPA - Datos Recibidos");
      console.log("📦 Viaje ID:", viaje?.id, "Código:", viaje?.codigoViaje);
      console.log("🔢 Total tramos:", tramos.length);

      if (primerTramo) {
        console.log("🟢 PRIMER TRAMO:");
        console.log("   - Tramo completo:", primerTramo);
        console.log(
          "   - Establecimiento Origen:",
          primerTramo.establecimientoOrigen,
        );
        if (primerTramo.establecimientoOrigen) {
          console.log(
            "     ✓ Nombre:",
            primerTramo.establecimientoOrigen.nombre,
          );
          console.log(
            "     ✓ Latitud:",
            primerTramo.establecimientoOrigen.latitud,
            `(${typeof primerTramo.establecimientoOrigen.latitud})`,
          );
          console.log(
            "     ✓ Longitud:",
            primerTramo.establecimientoOrigen.longitud,
            `(${typeof primerTramo.establecimientoOrigen.longitud})`,
          );
        }
      }

      if (ultimoTramo) {
        console.log("🔴 ÚLTIMO TRAMO:");
        console.log("   - Tramo completo:", ultimoTramo);
        console.log(
          "   - Establecimiento Destino:",
          ultimoTramo.establecimientoDestino,
        );
        if (ultimoTramo.establecimientoDestino) {
          console.log(
            "     ✓ Nombre:",
            ultimoTramo.establecimientoDestino.nombre,
          );
          console.log(
            "     ✓ Latitud:",
            ultimoTramo.establecimientoDestino.latitud,
            `(${typeof ultimoTramo.establecimientoDestino.latitud})`,
          );
          console.log(
            "     ✓ Longitud:",
            ultimoTramo.establecimientoDestino.longitud,
            `(${typeof ultimoTramo.establecimientoDestino.longitud})`,
          );
        }
      }
      console.groupEnd();

      // Validación crítica: Si no hay tramos o faltan los establecimientos, abortamos para evitar el crash
      if (
        !primerTramo?.establecimientoOrigen ||
        !ultimoTramo?.establecimientoDestino
      ) {
        console.warn(
          "⚠️ No se pueden renderizar puntos oficiales: Faltan datos de establecimientos.",
        );
        return;
      }

      // Usamos Number() o parseFloat() con un fallback a 0 para evitar errores de tipo
      let coordsOrigenOficial = {
        lat: parseFloat(primerTramo.establecimientoOrigen.latitud || 0),
        lng: parseFloat(primerTramo.establecimientoOrigen.longitud || 0),
      };

      let coordsDestinoOficial = {
        lat: parseFloat(ultimoTramo.establecimientoDestino.latitud || 0),
        lng: parseFloat(ultimoTramo.establecimientoDestino.longitud || 0),
      };

      // ⭐ DEBUG - Ver coordenadas procesadas
      console.group("📍 COORDENADAS PROCESADAS");
      console.log("🟢 Origen:", coordsOrigenOficial);
      console.log("🔴 Destino:", coordsDestinoOficial);
      console.log(
        "✅ Origen válido:",
        !isNaN(coordsOrigenOficial.lat) &&
          !isNaN(coordsOrigenOficial.lng) &&
          coordsOrigenOficial.lat !== 0,
      );
      console.log(
        "✅ Destino válido:",
        !isNaN(coordsDestinoOficial.lat) &&
          !isNaN(coordsDestinoOficial.lng) &&
          coordsDestinoOficial.lat !== 0,
      );
      console.groupEnd();

      // Validar que las coordenadas procesadas sean números válidos
      if (isNaN(coordsOrigenOficial.lat) || isNaN(coordsDestinoOficial.lat)) {
        console.error("❌ Coordenadas oficiales inválidas detectadas.");
        return;
      }

      // ⚠️ VALIDACIÓN ADICIONAL: Verificar que no sean (0,0)
      if (coordsOrigenOficial.lat === 0 || coordsDestinoOficial.lat === 0) {
        console.error("❌ Coordenadas en (0,0) detectadas - datos incompletos del backend");
        console.error("   - Origen:", coordsOrigenOficial);
        console.error("   - Destino:", coordsDestinoOficial);
        return;
      }

      // Todo validado — recién ahora destruimos el mapa anterior
      clearMap();

      // 2. FILTRAR TRACKING (Para la línea azul)
      const puntosGPSFiltrados = (tracking || [])
        .map((p) => ({
          lat: parseFloat(p.latitude),
          lng: parseFloat(p.longitude),
          gpstime: p.gpstime,
          isDemo: p.isDemo,
          time: p.gpstime ? new Date(p.gpstime * 1000) : null,
          timestampStr: p.gpstime
            ? new Date(p.gpstime * 1000).toLocaleString("es-PE", { timeZone: "America/Lima" })
            : "N/A",
        }))
        .filter((p) => {
          if (isNaN(p.lat) || isNaN(p.lng)) return false; // coords inválidas
          // Durante demo startDemo: solo tracks de esta sesión (por tiempo de inicio)
          if (demoActivoRef.current && demoStartTimeRef.current) {
            return p.gpstime >= demoStartTimeRef.current;
          }
          return true;
        })
        .sort((a, b) => a.gpstime - b.gpstime); // ascendente → polyline origen→destino, camión en posición actual

      console.log("📊 Datos GPS:", { total: tracking?.length || 0, filtrados: puntosGPSFiltrados.length });

      // 3. PREPARAR TÍTULOS
      const horaLlegadaFormateada = primerTramo.horaLlegadaReal
        ? new Date(primerTramo.horaLlegadaReal).toLocaleString("es-PE", { timeZone: "America/Lima" })
        : "Sin reporte de llegada";

      const ultimaActualizacionGPS =
        puntosGPSFiltrados.length > 0
          ? puntosGPSFiltrados[puntosGPSFiltrados.length - 1].timestampStr
          : "En trayecto";

      // --- MARCADOR INICIO (I) ---
      console.log("🟢 Creando marcador INICIO en:", coordsOrigenOficial);
      const startMarker = new window.google.maps.Marker({
        position: coordsOrigenOficial,
        map: window.trackingMap,
        title: `INICIO RUTA GPS - LLEGADA REAL: ${horaLlegadaFormateada}`,
        icon: {
          path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
          fillColor: "#16a34a",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
          scale: 1.5,
          anchor: new window.google.maps.Point(12, 24),
        },
        label: {
          text: "I",
          color: "white",
          fontWeight: "bold",
          fontSize: "14px",
        },
      });
      window.trackingMarkers.push(startMarker);
      bounds.extend(coordsOrigenOficial);

      // --- MARCADOR DESTINO (F) ---
      console.log("🔴 Creando marcador DESTINO en:", coordsDestinoOficial);
      const endMarker = new window.google.maps.Marker({
        position: coordsDestinoOficial,
        map: window.trackingMap,
        title: `FIN RUTA GPS - ÚLTIMO REPORTE: ${ultimaActualizacionGPS}`,
        icon: {
          path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
          fillColor: "#dc2626",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
          scale: 1.5,
          anchor: new window.google.maps.Point(12, 24),
        },
        label: {
          text: "F",
          color: "white",
          fontWeight: "bold",
          fontSize: "14px",
        },
      });
      window.trackingMarkers.push(endMarker);
      bounds.extend(coordsDestinoOficial);

      // --- LÍNEA DE RUTA Y MARCADOR CAMIÓN ---
      if (puntosGPSFiltrados.length > 0) {
        console.log(
          "🔵 Creando polyline con",
          puntosGPSFiltrados.length,
          "puntos",
        );
        window.trackingPolyline = new window.google.maps.Polyline({
          path: puntosGPSFiltrados,
          geodesic: true,
          strokeColor: "#2563eb",
          strokeOpacity: 0.8,
          strokeWeight: 4,
          map: window.trackingMap,
        });

        const ultimaPos = puntosGPSFiltrados[puntosGPSFiltrados.length - 1];
        const prevTruckPos = window.truckMarker ? window.truckMarker.getPosition() : null;
        console.log("🚛 Creando marcador CAMIÓN en:", ultimaPos);
        const truckMarker = new window.google.maps.Marker({
          position: { lat: ultimaPos.lat, lng: ultimaPos.lng },
          map: window.trackingMap,
          title: `VEHÍCULO: ${viaje?.vehiculo?.placa || "S/N"}`,
          icon: {
            path: "M20,8h-3V4H3C1.89,4 1,4.89 1,6v11h2c0,1.66 1.34,3 3,3s3,-1.34 3,-3h6c0,1.66 1.34,3 3,3s3,-1.34 3,-3h2V13L20,8zM6,18.5c-0.83,0 -1.5,-0.67 -1.5,-1.5s0.67,-1.5 1.5,-1.5s1.5,0.67 1.5,1.5S6.83,18.5 6,18.5zM18,18.5c-0.83,0 -1.5,-0.67 -1.5,-1.5s0.67,-1.5 1.5,-1.5s1.5,0.67 1.5,1.5S18.83,18.5 18,18.5zM17,5v3h2.5l1.96,2.5H17V9H9V7h8V5z",
            fillColor: "#2563eb",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
            scale: 1.2,
            anchor: new window.google.maps.Point(12, 12),
          },
        });
        window.truckMarker = truckMarker;
        window.trackingMarkers.push(truckMarker);
        if (prevTruckPos) {
          animateMarker(
            truckMarker,
            { lat: prevTruckPos.lat(), lng: prevTruckPos.lng() },
            { lat: ultimaPos.lat, lng: ultimaPos.lng },
            2000
          );
        }
        puntosGPSFiltrados.forEach((p) => bounds.extend(p));
      }

      if (!bounds.isEmpty()) {
        window.trackingMap.fitBounds(bounds, { padding: 60 });
        console.log("✅ Mapa ajustado a los límites (bounds)");
      }

      console.log("✅ Mapa renderizado exitosamente");
    } catch (error) {
      console.error("❌ Error actualizando mapa:", error);
      console.error("Stack trace:", error.stack);
    }
  };
  const loadVehicleTracking = async (vehiculoId, viaje) => {
    if (!vehiculoId || !fechaInicio || !fechaFin) return;
    // Snapshot del estado de demo al iniciar el fetch
    const demoActivoSnap = demoActivoRef.current;
    const demoStartSnap = demoStartTimeRef.current;

    try {
      setLoadingTracking(true);

      const inicio = fechaInicio.includes("T")
        ? fechaInicio
        : `${fechaInicio}T00:00:00`;
      const fin = fechaFin.includes("T") ? fechaFin : `${fechaFin}T23:59:59`;

      const trackingUrl = `${API_BASE_URL}/api/tracks-by-vehicle?vehiculoId=${vehiculoId}&inicio=${inicio}&fin=${fin}`;

      console.log("📡 Cargando datos GPS del vehículo...", { inicio, fin });

      const response = await fetch(trackingUrl, {
        headers: getApiHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("✅ Datos GPS recibidos:", data.length, "registros");
        setTrackingData(data);
       // Si demo activa y ya inicializada, y los marcadores en el mapa ya existen: no tocar el mapa, actualizarPosicionDemo se encarga
       if (simDemoActivoRef.current && !simDemoInicializandoRef.current && window.trackingMarkers && window.trackingMarkers.length > 0) return;
     // Primera carga al iniciar demo: limpiar tracks históricos y dibujar markers en origen
     if (simDemoInicializandoRef.current) {
    simDemoInicializandoRef.current = false;
    demoTracksAccRef.current = [];
    clearMap();
    if (window.truckMarker) { window.truckMarker.setMap(null); window.truckMarker = null; }
    if (window.trackingPolyline) { window.trackingPolyline.setMap(null); window.trackingPolyline = null; }
    if (window.trackingMarkers) { window.trackingMarkers.forEach(m => m.setMap(null)); window.trackingMarkers = []; }
    // Dibujar markers I/F + vehículo en origen — el que limpia también recrea
    if (window.google && window.trackingMap && viaje) {
      const tramos = viaje.tramos || [];
      const origen = tramos[0]?.establecimientoOrigen;
      const destino = tramos[tramos.length - 1]?.establecimientoDestino;
      if (origen?.latitud && destino?.latitud) {
        const oCoords = { lat: parseFloat(origen.latitud), lng: parseFloat(origen.longitud) };
        const dCoords = { lat: parseFloat(destino.latitud), lng: parseFloat(destino.longitud) };
        window.trackingMarkers = [];
        const iconPath = "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z";
        window.trackingMarkers.push(new window.google.maps.Marker({
          position: oCoords, map: window.trackingMap, title: 'INICIO RUTA',
          icon: { path: iconPath, fillColor: '#16a34a', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2, scale: 1.5, anchor: new window.google.maps.Point(12, 24) },
          label: { text: 'I', color: 'white', fontWeight: 'bold', fontSize: '14px' }
        }));
        window.trackingMarkers.push(new window.google.maps.Marker({
          position: dCoords, map: window.trackingMap, title: 'FIN RUTA',
          icon: { path: iconPath, fillColor: '#dc2626', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2, scale: 1.5, anchor: new window.google.maps.Point(12, 24) },
          label: { text: 'F', color: 'white', fontWeight: 'bold', fontSize: '14px' }
        }));
        // Vehículo en origen al arrancar la demo
        const truckMarker = new window.google.maps.Marker({
          position: oCoords, map: window.trackingMap,
          title: `VEHÍCULO: ${viaje?.vehiculo?.placa || 'S/N'}`,
          icon: {
            path: "M20,8h-3V4H3C1.89,4 1,4.89 1,6v11h2c0,1.66 1.34,3 3,3s3,-1.34 3,-3h6c0,1.66 1.34,3 3,3s3,-1.34 3,-3h2V13L20,8zM6,18.5c-0.83,0 -1.5,-0.67 -1.5,-1.5s0.67,-1.5 1.5,-1.5s1.5,0.67 1.5,1.5S6.83,18.5 6,18.5zM18,18.5c-0.83,0 -1.5,-0.67 -1.5,-1.5s0.67,-1.5 1.5,-1.5s1.5,0.67 1.5,1.5S18.83,18.5 18,18.5zM17,5v3h2.5l1.96,2.5H17V9H9V7h8V5z",
            fillColor: '#2563eb', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2, scale: 1.2,
            anchor: new window.google.maps.Point(12, 12),
          },
        });
        window.truckMarker = truckMarker;
        window.trackingMarkers.push(truckMarker);
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(oCoords);
        bounds.extend(dCoords);
        window.trackingMap.fitBounds(bounds);
      }
    }
    return;
}

        // Si la demo se detuvo mientras el fetch estaba en vuelo, no dibujar con datos obsoletos
        if (demoActivoSnap && !demoActivoRef.current) return;

        if (data.length > 0) {
          try { updateMapWithTracking(data, viaje); } catch (e) { console.error("❌ updateMapWithTracking error:", e); }
        } else {
          clearMap();
          showToast("No hay datos GPS para el período seleccionado", "error");
        }
      } else {
        console.error("❌ Error cargando datos GPS:", response.status);
        setTrackingData([]);
        clearMap();
      }
    } catch (error) {
      console.error("Error de conexión al cargar GPS:", error);
      setTrackingData([]);
      clearMap();
      showToast("Error de conexión al cargar datos GPS", "error");
    } finally {
      setLoadingTracking(false);
    }
  };

  // Asegúrate de que esta función esté definida en el scope del componente
  // ✅ FUNCIÓN CRÍTICA CORREGIDA: Filtrar datos GPS desde la llegada al origen
  const getValidTrackingData = (trackingData, selectedViaje) => {
    const primerTramo = selectedViaje?.tramos?.[0];
    const ultimoTramo =
      selectedViaje?.tramos?.[selectedViaje.tramos.length - 1];

    // ⚠️ SI NO HAY HORA DE LLEGADA REAL AL ORIGEN
    if (!primerTramo?.horaLlegadaReal) {
      console.warn("⚠️ No hay hora de llegada al origen");
      return [];
    }

    const horaInicio = parseDateLocal(primerTramo.horaLlegadaReal);

    if (!horaInicio) {
      console.warn("⚠️ No se pudo parsear la hora de llegada al origen");
      return [];
    }

    // ✅ DETERMINAR HORA DE FIN SEGÚN ESTADO DEL VIAJE
    let horaFin = null;

    if (
      selectedViaje.estado === "completado" &&
      ultimoTramo?.horaSalidaRealDestino
    ) {
      // Si el viaje está completado, usar la hora de salida del destino
      horaFin = parseDateLocal(ultimoTramo.horaSalidaRealDestino);
      console.log(
        "✅ Viaje completado - Usando hora de salida del destino:",
        horaFin,
      );
    }
    // Si no está completado, horaFin queda null y toma todos los puntos hasta ahora

    // Filtrar puntos GPS entre horaInicio y horaFin (si existe)
    const datosValidos = trackingData.filter((punto) => {
      let fechaPunto = null;

      if (punto.creationDate) {
        const dateStr = punto.creationDate.includes("Z")
          ? punto.creationDate
          : punto.creationDate + "Z";
        fechaPunto = new Date(dateStr);
      } else if (punto.gpstime) {
        fechaPunto = new Date(punto.gpstime * 1000);
      }

      if (!fechaPunto) return false;

      const despuesDelInicio = fechaPunto.getTime() >= horaInicio.getTime();
      const antesDelFin = !horaFin || fechaPunto.getTime() <= horaFin.getTime();

      return despuesDelInicio && antesDelFin;
    });

    console.log(
      `✅ Datos GPS filtrados: ${datosValidos.length} de ${trackingData.length} puntos`,
    );
    console.log(`   Desde: ${horaInicio.toLocaleString("es-PE")}`);
    console.log(
      `   Hasta: ${horaFin ? horaFin.toLocaleString("es-PE") : "Ahora (en curso)"}`,
    );

    return datosValidos;
  };

  // Función utilitaria para formatear la fecha GPS (como la usaste en el ejemplo anterior)
  const formatTimestamp = (punto) => {
    if (punto?.creationDate || punto?.gpstime) {
      const timestamp = punto.creationDate
        ? punto.creationDate
        : punto.gpstime * 1000;

      const fecha = new Date(timestamp);
      if (isNaN(fecha.getTime())) return "N/A";

      return fecha.toLocaleDateString("es-PE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return "N/A";
  };
  const convertirALocal = (fechaUTC) => {
    if (!fechaUTC) return null;
    return new Date(fechaUTC).toISOString().slice(0, 16); // datetime-local
  };

  // skipClear: mantiene datos actuales mientras carga. silent: no muestra spinner (usado en demo polling)
  const fetchViajeDetalle = async (viajeId, skipClear = false, silent = false, skipMap = false) => {
    if (!viajeId) return;

    if (!silent) setIsLoading(true);
    if (!skipClear) {
      setSelectedViaje(null);
      clearMap();
      setTrackingData([]);
    }

    try {
      const apiUrl = `${API_BASE_URL}/api/viajes/${viajeId}`; // Endpoint COMPLETO
      console.log(`📡 Solicitando detalle: ${apiUrl}`);

      const res = await fetch(apiUrl, { headers: getApiHeaders() });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const viajeCompleto = await res.json();
      console.log("✅ Detalle del viaje cargado.");

      // Procesamiento normal...
      const viajeProcesado = {
        ...viajeCompleto,
        // ✅ Los tramos vienen aquí y se ordenan
        tramos: viajeCompleto.tramos
          ? [...viajeCompleto.tramos].sort((a, b) => a.orden - b.orden)
          : [],
        progreso: calcularProgreso(viajeCompleto.tramos),
        riesgo: calcularRiesgo(viajeCompleto),
        delay: calcularDelay(viajeCompleto),
      };

      setSelectedViaje(viajeProcesado);
      setIsLoading(false);

      // Tracking carga sin bloquear — el panel ya es visible
      if (viajeProcesado.vehiculo?.id && !skipMap) {
        loadVehicleTracking(viajeProcesado.vehiculo.id, viajeProcesado);
      } else if (!skipMap) {
        setTrackingData([]);
        clearMap();
      }
    } catch (err) {
      console.error("❌ ERROR cargando detalle:", err);
      showToast(`Error: ${err.message}`, "error");
      setSelectedViaje(null);
      setIsLoading(false);
    }
  };
  const fetchViajes = async () => {
    if (!user) return;

    try {
      setLoading(true);

      let apiUrl;
      switch (user.tipo) {
        case "transportista":
          apiUrl = `${API_BASE_URL}/api/viajes/lite/empresa/${user.empresa_id}`;
          break;
        case "operador_logistico":
          apiUrl = `${API_BASE_URL}/api/viajes/lite/operador/${user.empresa_id}`;
          break;
        case "exportador":
        case "cliente":
          apiUrl = `${API_BASE_URL}/api/viajes/lite/cliente/${user.empresa_id}`;
          break;
        default:
          console.error("Tipo no reconocido:", user.tipo);
          return;
      }

      console.log("📡 Cargando viajes (LIGERO)...");

      const res = await fetch(apiUrl, { headers: getApiHeaders() });

      const data = await res.json();

      // Validamos que data sea un array para evitar errores de .map
      if (!Array.isArray(data)) {
        setViajes([]);
        return;
      }

      // 1. Mapeamos la data básica (Versión Lite)
      const viajesConSeguimiento = data.map((viaje) => ({
        ...viaje,
        origenRuta: { nombre: viaje.nombreOrigenRuta || "Origen Desconocido" },
        destinoRuta: {
          nombre: viaje.nombreDestinoRuta || "Destino Desconocido",
        },
        tramos: [], // Lite no trae tramos
        progreso: 0,
        riesgo: "BAJO",
        delay: "A tiempo",
      }));

      // 2. Lógica de Filtrado: Solo comparar FECHA de inicio (sin hora)
      const viajesFiltrados = viajesConSeguimiento.filter((v) => {
        if (!v.fechaInicioProgramada) return false;

        // Extraer solo la fecha YYYY-MM-DD del string (antes de la T o espacio)
        const fechaViajeStr = v.fechaInicioProgramada
          .split("T")[0]
          .split(" ")[0];

        // Si hay filtros de fecha, aplicarlos
        if (fechaInicio && fechaFin) {
          // Comparar directamente las fechas en formato YYYY-MM-DD
          return fechaViajeStr >= fechaInicio && fechaViajeStr <= fechaFin;
        }

        return true; // Si no hay filtros, mostrar todo
      });

      setViajes(viajesFiltrados);

      // 3. Lógica de Autoselección
      // if (viajesFiltrados.length > 0) {
      //   if (selectedViaje) {
      //     const viajeActualizado = viajesFiltrados.find(
      //       (v) => v.id === selectedViaje.id
      //     );

      //     if (viajeActualizado) {
      //       console.log(
      //         "✅ Manteniendo viaje seleccionado, refrescando detalles..."
      //       );
      //       // Recargamos el detalle FULL para tener tracking actualizado
      //       await fetchViajeDetalle(selectedViaje.id, true);
      //     } else {
      //       console.log(
      //         "✅ Viaje anterior fuera de rango, seleccionando nuevo..."
      //       );
      //       await fetchViajeDetalle(viajesFiltrados[0].id);
      //     }
      //   } else {
      //     console.log("✅ Autoseleccionando primer viaje de la lista...");
      //     await fetchViajeDetalle(viajesFiltrados[0].id);
      //   }
      // } else {
      //   // Si no hay viajes después de filtrar
      //   setSelectedViaje(null);
      //   setTrackingData([]);
      //   clearMap();
      // }
    } catch (err) {
      console.error("❌ Error cargando viajes:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchViajes();
  }, [user, fechaInicio, fechaFin]);

  const calcularProgreso = (tramos) => {
    if (!tramos || tramos.length === 0) return 0;
    const completados = tramos.filter((t) => t.estado === "completado").length;
    return Math.round((completados / tramos.length) * 100);
  };

  const calcularRiesgo = (viaje) => {
    const now = new Date();
    const fechaFin = new Date(viaje.fechaFinProgramada);
    const horasRestantes = (fechaFin - now) / (1000 * 60 * 60);

    if (horasRestantes < 2) return "alto";
    if (horasRestantes < 6) return "medio";
    return "bajo";
  };

  const calcularDelay = (viaje) => {
    if (!viaje.fechaFinProgramada) return 0;
    const now = new Date();
    const fechaProgramada = new Date(viaje.fechaFinProgramada);
    if (now > fechaProgramada && viaje.estado !== "completado") {
      return Math.round((now - fechaProgramada) / (1000 * 60));
    }
    return 0;
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case "pendiente":
        return "bg-blue-100 text-blue-800";
      case "en_transito":
        return "bg-orange-100 text-orange-800";
      case "completado":
        return "bg-green-100 text-green-800";
      case "retrasado":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case "pendiente":
        return <Clock className="h-4 w-4" />;
      case "en_transito":
        return <Truck className="h-4 w-4" />;
      case "completado":
        return <CheckCircle className="h-4 w-4" />;
      case "retrasado":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const getRiesgoColor = (riesgo) => {
    switch (riesgo) {
      case "bajo":
        return "bg-green-100 text-green-800";
      case "medio":
        return "bg-yellow-100 text-yellow-800";
      case "alto":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  // const parseDateLocal = (dateString) => {
  //   if (!dateString) return null;
  //   return new Date(dateString); // SIN 'Z'
  // };
  const parseDateLocal = (dateString) => {
    if (!dateString) return null;
    // Normalizar a ISO con Z para que el browser interprete como UTC,
    // y luego Intl.DateTimeFormat con timeZone:"America/Lima" convierte correctamente.
    let normalized = dateString.trim()
      .replace(/\.[\d]+Z?$/, "") // quitar microsegundos
      .replace(" ", "T");         // formato MySQL "YYYY-MM-DD HH:MM:SS" → ISO
    if (!/Z$/.test(normalized) && !/[+-]\d{2}:\d{2}$/.test(normalized)) {
      normalized += "Z"; // sin indicador de zona → asumir UTC
    }
    const date = new Date(normalized);
    return isNaN(date.getTime()) ? null : date;
  };
  const formatDate = (dateString) => {
    const date = parseDateLocal(dateString);
    if (!date) return "Sin fecha";

    return new Intl.DateTimeFormat("es-PE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "America/Lima",
    }).format(date);
  };

  const formatTime = (dateString) => {
    const date = parseDateLocal(dateString);
    if (!date) return "--:--";

    return new Intl.DateTimeFormat("es-PE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "America/Lima",
    }).format(date);
  };

  const clientesUnicos = [
    ...new Set(viajes.map((v) => v.empresaCliente?.nombre).filter(Boolean)),
  ];
  const transportistasUnicos = [
    ...new Set(
      viajes.map((v) => v.empresaTransportista?.nombre).filter(Boolean),
    ),
  ];
  const operadoresUnicos = [
    ...new Set(viajes.map((v) => v.empresaOperador?.nombre).filter(Boolean)),
  ];

  const viajesFiltrados = viajes.filter((viaje) => {
    const matchesEstado =
      filtroEstado === "all" || viaje.estado === filtroEstado;
    const matchesCliente =
      filtroCliente === "all" || viaje.empresaCliente?.nombre === filtroCliente;
    const matchesTransportista =
      filtroTransportista === "all" ||
      viaje.empresaTransportista?.nombre === filtroTransportista;
    const matchesOperador =
      filtroOperador === "all" ||
      viaje.empresaOperador?.nombre === filtroOperador;
    function parseLocalDate(dateString) {
      if (!dateString) return null;

      // divide la fecha para evitar que JS la interprete como UTC
      const [datePart, timePart] = dateString.split("T");
      const [year, month, day] = datePart.split("-").map(Number);

      if (timePart) {
        const [h, m, s] = timePart.split(":").map(Number);
        return new Date(year, month - 1, day, h, m, s || 0);
      }

      return new Date(year, month - 1, day);
    }

    let matchesFecha = true;
    if (fechaInicio || fechaFin) {
      const viajeDate = parseLocalDate(viaje.fechaInicioProgramada);

      if (fechaInicio) {
        const fechaInicioCompleta = fechaInicio.includes("T")
          ? parseLocalDate(fechaInicio)
          : parseLocalDate(fechaInicio + "T00:00:00");

        matchesFecha = matchesFecha && viajeDate >= fechaInicioCompleta;
      }

      if (fechaFin) {
        const fechaFinCompleta = fechaFin.includes("T")
          ? parseLocalDate(fechaFin)
          : parseLocalDate(fechaFin + "T23:59:59");

        matchesFecha = matchesFecha && viajeDate <= fechaFinCompleta;
      }
    }

    return (
      matchesEstado &&
      matchesCliente &&
      matchesTransportista &&
      matchesOperador &&
      matchesFecha
    );
  });

  const viajesCompletados = viajesFiltrados.filter(
    (v) => v.estado === "completado",
  ).length;
  const totalViajes = viajesFiltrados.length;

  const startDemo = async () => {
    if (!selectedViaje) {
      showToast("Seleccioná un viaje primero", "error");
      return;
    }
    try {
      showToast("Iniciando demo...");
      const res = await fetch(`${API_BASE_URL}/api/demo/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ viajeId: selectedViaje.id, intervalo: 3 }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Error al iniciar demo");
      }
      const data = await res.json();
      setDemoSession(data.sessionId);
      setDemoOrigen({ lat: data.origenLat, lng: data.origenLon });
      setDemoDestino({ lat: data.destinoLat, lng: data.destinoLon });
      setDemoActivo(true);
      demoActivoRef.current = true;
      demoStartTimeRef.current = Math.floor(Date.now() / 1000);
      setTrackingData([]);
      clearMap();

      // Disparar monitoreo inmediato en Laravel para generar llegada_origen al instante
      console.log("🚀 Llamando monitorear-inicio:", `${LARAVEL_API_URL}/eventos/demo/monitorear-inicio`, "viajeId:", selectedViaje.id);
      fetch(`${LARAVEL_API_URL}/eventos/demo/monitorear-inicio`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ viajeId: selectedViaje.id }),
      }).then(r => {
        console.log("🔔 monitorear-inicio (origen):", r.status);
        if (!r.ok) r.text().then(t => console.error("❌ monitorear-inicio error:", t));
      }).catch(e => console.error("❌ monitorear-inicio fetch error:", e));

      // Refresh panel immediately with the reset state from the backend
      const viajeIdSnapshot = selectedViaje.id;
      await fetchViajeDetalle(viajeIdSnapshot, true, true);

      // GPS polling every 5s — viaje detail refresh every 15s (every 3 cycles) — silent during demo
      let pollCycle = 0;
      demoPollingRef.current = setInterval(async () => {
        pollCycle++;
        // Always refresh GPS tracking
        await loadVehicleTracking(selectedViaje.vehiculo?.id, selectedViaje);
        // Refresh viaje detail (tramos, ETA, estados) every 15s
        if (pollCycle % 3 === 0) {
          await fetchViajeDetalle(viajeIdSnapshot, true, true);
        }
        // Check if demo completed naturally
        try {
          const statusRes = await fetch(`${API_BASE_URL}/api/demo/status/${data.sessionId}`);
          const status = await statusRes.json();
          if (status.completado || !status.active) {
            clearInterval(demoPollingRef.current);
            demoPollingRef.current = null;
            // Disparar monitoreo inmediato en Laravel para LLEGADA_CONFIRMADA_DESTINO
            fetch(`${LARAVEL_API_URL}/eventos/demo/monitorear-inicio`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
              },
              body: JSON.stringify({ viajeId: viajeIdSnapshot }),
            }).then(r => {
              console.log("🔔 monitorear-inicio (destino):", r.status);
              if (!r.ok) r.text().then(t => console.error("❌ monitorear-inicio destino error:", t));
            }).catch(e => console.error("❌ monitorear-inicio destino fetch error:", e));
            // Stop backend session (use data.sessionId — demoSession state is stale in this closure)
            try {
              await fetch(`${API_BASE_URL}/api/demo/stop/${data.sessionId}`, { method: "POST" });
            } catch (_) {}
            // Reset frontend state
            setDemoActivo(false);
            setDemoSession(null);
            setDemoOrigen(null);
            setDemoDestino(null);
            setTrackingData([]);
            await fetchViajeDetalle(viajeIdSnapshot, true);
            showToast("Demo completada — vehículo llegó a destino", "success");
          }
        } catch (_) {}
      }, 5000);

      showToast("Demo iniciada", "success");
    } catch (e) {
      showToast(e.message, "error");
    }
  };

  const stopDemo = async () => {
    if (demoPollingRef.current) {
      clearInterval(demoPollingRef.current);
      demoPollingRef.current = null;
    }
    if (demoSession) {
      try {
        await fetch(`${API_BASE_URL}/api/demo/stop/${demoSession}`, { method: "POST" });
      } catch (_) {}
    }
    setDemoActivo(false);
    demoActivoRef.current = false;
    demoStartTimeRef.current = null;
    setDemoSession(null);
    setDemoOrigen(null);
    setDemoDestino(null);
    setTrackingData([]);
    if (selectedViaje) await fetchViajeDetalle(selectedViaje.id);
    showToast("Demo detenida");
  };

  // ── Simulación GPS ──────────────────────────────────────────────────────────

  const initDemo = async () => {
    if (!selectedViaje) { showToast("Seleccioná un viaje primero", "error"); return; }
    try {
      setSimLoading(true);
      simDemoInicializandoRef.current = true;
      clearMap();
if (window.truckMarker) { 
    window.truckMarker.setMap(null); 
    window.truckMarker = null; 
}
if (window.trackingPolyline) {
    window.trackingPolyline.setMap(null);
    window.trackingPolyline = null;
}
// Limpiar todos los markers residuales
if (window.trackingMarkers) {
    window.trackingMarkers.forEach(m => m.setMap(null));
    window.trackingMarkers = [];
}
demoTracksAccRef.current = [];
setTrackingData([]);
      const res = await fetch(`${API_BASE_URL}/api/demo/start`, {
        method: "POST",
        headers: getApiHeaders(),
        body: JSON.stringify({ viajeId: selectedViaje.id, intervalo: simDemoIntervalo }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || "Error al iniciar demo"); }
      const data = await res.json();
      const origenCoords  = data.origenLat  ? { lat: data.origenLat,  lng: data.origenLon  } : null;
      const destinoCoords = data.destinoLat ? { lat: data.destinoLat, lng: data.destinoLon } : null;
      simDemoSessionIdRef.current = data.sessionId;
      setSimDemoActivo(true);
      simDemoActivoRef.current = true;
      simDemoViajeIdRef.current = selectedViaje.id;
      const sessionStartTime = Math.floor(Date.now() / 1000);
      localStorage.setItem(simStorageKey(selectedViaje.id), JSON.stringify({
        sessionId: data.sessionId, viajeId: selectedViaje.id,
        vehiculoId: data.vehiculoId || selectedViaje.vehiculo?.id,
        sessionStartTime,
        pasoActual: 0, origenCoords, destinoCoords, modo: "demo", intervalSeconds: simDemoIntervalo,
      }));

      // Notificar a Laravel para monitorear inmediatamente (llegada origen)
      console.log("🚀 [initDemo] Llamando monitorear-inicio, viajeId:", selectedViaje.id);
      fetch(`${LARAVEL_API_URL}/eventos/demo/monitorear-inicio`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ viajeId: selectedViaje.id }),
      }).then(r => {
        console.log("🔔 [initDemo] monitorear-inicio:", r.status);
        if (!r.ok) r.text().then(t => console.error("❌ monitorear-inicio error:", t));
      }).catch(e => console.error("❌ monitorear-inicio fetch error:", e));

      const viajeIdDemo = selectedViaje.id;
      simDemoPollRef.current = setInterval(() => {
        if (selectedViajeIdRef.current === viajeIdDemo) {
          fetchViajeDetalle(viajeIdDemo, true, true, true);
          actualizarPosicionDemo(viajeIdDemo);
        }
      }, simDemoIntervalo * 1000);

      // loadVehicleTracking (llamado dentro de fetchViajeDetalle) dibuja los markers I/F al hacer su clearMap
      await fetchViajeDetalle(selectedViaje.id, true, true);
      showToast(`Demo iniciado — 1 track cada ${simDemoIntervalo}s`, "success");
    } catch (e) { showToast(e.message, "error"); }
    finally { setSimLoading(false); }
  };

  const simularAlertasGPS = async () => {
    const viajeId = simDemoViajeIdRef.current;
    if (!viajeId) return;
    setSimAlertasLoading(true);
    try {
      const res = await fetch(`${LARAVEL_API_URL}/eventos/demo/simular-alertas-gps`, {
        method: 'POST',
        headers: { ...getApiHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ viajeId }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(`✅ ${data.notificaciones_enviadas} notificaciones enviadas (${data.tipos_simulados?.length ?? 0} tipos)`, 'success');
      } else {
        showToast('Error al simular alertas', 'error');
      }
    } catch {
      showToast('Error de conexión al simular alertas', 'error');
    } finally {
      setSimAlertasLoading(false);
    }
  };

  const stopDemoSim = async (silent = false) => {
    if (!silent) {
      const result = await Swal.fire({
        title: "¿Detener la demo?",
        html: `
          <div class="text-left">
            <p>El scheduler en el servidor se detendrá y <strong>no podrá reanudarse</strong>.</p>
            <p class="mt-2 text-orange-600">⚠️ Si querés pausar y continuar mañana, simplemente <strong>cerrá la aplicación sin tocar este botón</strong>.</p>
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, detener",
        cancelButtonText: "Cancelar",
        reverseButtons: true,
      });
      if (!result.isConfirmed) return;
    }

    if (simDemoPollRef.current) { clearInterval(simDemoPollRef.current); simDemoPollRef.current = null; }
    if (simDemoSessionIdRef.current) {
      try { await fetch(`${API_BASE_URL}/api/demo/stop/${simDemoSessionIdRef.current}`, { method: "POST", headers: getApiHeaders() }); } catch (_) {}
    }
    simDemoSessionIdRef.current = null;
    if (selectedViaje) localStorage.removeItem(simStorageKey(selectedViaje.id));
  if (window.trackingMarkers) {
    window.trackingMarkers.forEach(m => m.setMap(null));
    window.trackingMarkers = [];
}
    if (window.trackingPolyline) { window.trackingPolyline.setMap(null); window.trackingPolyline = null; }
    setSimDemoActivo(false);
    simDemoActivoRef.current = false;
    simDemoViajeIdRef.current = null;
    demoTracksAccRef.current = [];
    if (!silent) showToast("Demo detenido");
  };


  const simStorageKey = (viajeId) => `vilox_sim_${viajeId}`;


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg backdrop-blur-sm max-w-sm animate-in slide-in-from-right-full duration-300 ${
              toast.type === "error"
                ? "bg-red-500/90 text-white border border-red-400"
                : "bg-emerald-500/90 text-white border border-emerald-400"
            }`}
          >
            {toast.type === "error" ? (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              className="ml-auto p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <AlertTriangle className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="bg-white shadow-sm border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={onVolverAtras}
                className="p-2 hover:bg-gray-100 rounded-lg mr-3 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div className="bg-orange-500 p-2 rounded-lg mr-3">
                <Navigation className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Seguimiento GPS - Google Maps
                </h1>
                <p className="text-sm text-gray-600">
                  {user?.tipo === "transportista" && "Vista Transportista"}
                  {user?.tipo === "operador_logistico" &&
                    "Vista Operador Logístico"}
                  {(user?.tipo === "exportador" || user?.tipo === "cliente") &&
                    "Vista Cliente"}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-sm">
                <div
                  className={`w-3 h-3 rounded-full ${
                    window.trackingMap ? "bg-green-500" : "bg-red-500"
                  }`}
                ></div>
                <span className="text-gray-600">
                  {window.trackingMap ? "Google Maps OK" : "Cargando..."}
                </span>
              </div>
              <button
                onClick={() => {
                  setLoading(true);
                  fetchViajes();
                  setTimeout(() => setLoading(false), 1000);
                }}
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                />
                Refrescar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        <div className="bg-white rounded-lg shadow-sm p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900 flex items-center text-sm">
              <Filter className="h-4 w-4 mr-2" />
              Filtros de búsqueda
            </h3>
            <span className="text-sm text-gray-600">
              {viajesFiltrados.length} viajes encontrados
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                className="w-full p-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">Todos</option>
                <option value="pendiente">Pendiente</option>
                <option value="en_transito">En Tránsito</option>
                <option value="completado">Completado</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Cliente
              </label>
              <select
                value={filtroCliente}
                onChange={(e) => setFiltroCliente(e.target.value)}
                className="w-full p-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">Todos</option>
                {clientesUnicos.map((cliente) => (
                  <option key={cliente} value={cliente}>
                    {cliente}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Transportista
              </label>
              <select
                value={filtroTransportista}
                onChange={(e) => setFiltroTransportista(e.target.value)}
                className="w-full p-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">Todos</option>
                {transportistasUnicos.map((transportista) => (
                  <option key={transportista} value={transportista}>
                    {transportista}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Generador de Carga
              </label>
              <select
                value={filtroOperador}
                onChange={(e) => setFiltroOperador(e.target.value)}
                className="w-full p-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">Todos</option>
                {operadoresUnicos.map((operador) => (
                  <option key={operador} value={operador}>
                    {operador}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Fecha Inicio
              </label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="w-full p-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Fecha Fin
              </label>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="w-full p-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setFiltroEstado("all");
                  setFiltroCliente("all");
                  setFiltroTransportista("all");
                  setFiltroOperador("all");
                  const today = new Date();
                  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                  setFechaInicio(todayStr);
                  setFechaFin(todayStr);
                }}
                className="w-full p-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-3 py-2 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900 flex items-center text-sm">
              <Package className="h-4 w-4 mr-2" />
              Listado de viajes
            </h3>
          </div>

          <div className="p-2 overflow-y-auto" style={{ height: "120px" }}>
            {loading ? (
              <div className="text-center py-4">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-blue-500" />
                <p className="text-sm text-gray-600">Cargando viajes...</p>
              </div>
            ) : viajesFiltrados.length === 0 ? (
              <div className="text-center py-4">
                <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600">
                  No hay viajes para mostrar
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {viajesFiltrados.map((viaje) => (
                  // ... (dentro del map de viajesFiltrados)

                  <div
                    key={viaje.id}
                    onClick={async () => {
                      // ⭐ EVITAR DOBLE CARGA SI YA ESTÁ SELECCIONADO
                      if (selectedViaje?.id === viaje.id) {
                        console.log("✅ Viaje ya seleccionado, no se recarga");
                        return;
                      }

                      console.log(`🔄 Cargando viaje: ${viaje.codigoViaje}`);
                      console.log("📅 Fechas del viaje:", {
                        inicio: viaje.fechaInicioProgramada,
                        fin: viaje.fechaFinProgramada,
                      });
                      await fetchViajeDetalle(viaje.id);
                    }}
                    className={`border rounded p-2 cursor-pointer transition-all hover:shadow-sm ${
                      selectedViaje?.id === viaje.id
                        ? "border-blue-500 bg-blue-50 shadow-sm"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center space-x-3">
                            <span className="font-semibold text-sm text-gray-900">
                              {viaje.codigoViaje}
                            </span>
                            <span className="text-gray-500">|</span>
                            <span className="font-medium text-gray-700">
                              {viaje.origenRuta?.nombre &&
                              viaje.destinoRuta?.nombre
                                ? `${viaje.origenRuta.nombre} → ${viaje.destinoRuta.nombre}`
                                : "Ruta no definida"}
                            </span>
                            <span className="text-gray-500">|</span>
                            <span className="text-gray-600">
                              <Truck className="h-3 w-3 inline mr-1" />
                              {viaje.placaVehiculo || "N/A"}
                            </span>
                            <span className="text-gray-500">|</span>
                            <span className="text-gray-600">
                              <span className="text-gray-500">Inicio:</span>{" "}
                              {formatDate(viaje.fechaInicioProgramada)}{" "}
                              {formatTime(viaje.fechaInicioProgramada)}
                            </span>
                            <span className="text-gray-500">|</span>
                            <span className="text-gray-600">
                              <span className="text-gray-500">Fin:</span>{" "}
                              {formatDate(viaje.fechaFinProgramada)}{" "}
                              {formatTime(viaje.fechaFinProgramada)}
                            </span>
                          </div>

                          <div className="flex items-center space-x-2">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(
                                viaje.estado,
                              )}`}
                            >
                              {getEstadoIcon(viaje.estado)}
                              <span className="ml-1">
                                {viaje.estado.replace("_", " ").toUpperCase()}
                              </span>
                            </span>
                            <span
                              className={`px-1.5 py-0.5 rounded text-xs font-medium ${getRiesgoColor(
                                viaje.riesgo,
                              )}`}
                            >
                              {viaje.riesgo === "bajo"
                                ? "🟢"
                                : viaje.riesgo === "medio"
                                  ? "🟡"
                                  : "🔴"}
                            </span>
                            {viaje.delay > 0 && (
                              <span className="text-xs text-orange-600 font-medium">
                                +{viaje.delay}min
                              </span>
                            )}
                          </div>
                        </div>

                        {/* ELIMINADA LÍNEA SEPARADA DE FECHAS */}

                        <div className="mt-1 flex items-center">
                          <div className="flex-1 mr-2">
                            <div className="w-full bg-gray-200 rounded-full h-1">
                              <div
                                className="bg-blue-500 h-1 rounded-full transition-all"
                                style={{ width: `${viaje.progreso}%` }}
                              />
                            </div>
                          </div>
                          <span className="text-xs text-gray-600">
                            {viaje.progreso}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="bg-white rounded-lg shadow-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900 flex items-center text-sm">
                <MapPin className="h-4 w-4 mr-2" />
                Google Maps - Seguimiento GPS
              </h3>
              {selectedViaje && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    {selectedViaje.codigoViaje}
                  </span>
                  <div
                    className={`w-2 h-2 rounded-full ${
                      trackingData.length > 0 && window.trackingMap
                        ? "bg-green-500 animate-pulse"
                        : "bg-red-500"
                    }`}
                  ></div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg border shadow-sm overflow-hidden relative">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-3 text-white">
                <h3 className="text-lg font-bold flex items-center">
                  <Navigation className="mr-2 h-5 w-5" />
                  Mapa de Seguimiento GPS
                </h3>
                <p className="text-blue-100 mt-1 text-sm">
                  {selectedViaje
                    ? trackingData.length > 0
                      ? `${selectedViaje.codigoViaje}: Ruta GPS del vehículo ${
                          selectedViaje.vehiculo?.placa || "N/A"
                        }`
                      : `${selectedViaje.codigoViaje}: Sin datos GPS para el período seleccionado`
                    : "Selecciona un viaje para ver los datos GPS del vehículo"}
                </p>
              </div>

              <div className="relative">
                <div
                  ref={mapContainerRef}
                  style={{ height: "400px", width: "100%" }}
                  className="google-map-container"
                />

                {!selectedViaje && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm z-10">
                    <div className="text-center">
                      <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Selecciona un Viaje
                      </h3>
                      <p className="text-gray-600 max-w-md">
                        Elige un viaje de la lista para visualizar los datos GPS
                        del vehículo en el período seleccionado
                      </p>
                    </div>
                  </div>
                )}

                {selectedViaje &&
                  trackingData.length === 0 &&
                  !loadingTracking && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm z-10">
                      <div className="text-center">
                        <AlertTriangle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Sin Datos GPS
                        </h3>
                        <p className="text-gray-600 max-w-md">
                          No hay registros GPS del vehículo{" "}
                          {selectedViaje.vehiculo?.placa || "N/A"} para el
                          período {formatDate(fechaInicio)} -{" "}
                          {formatDate(fechaFin)}
                        </p>
                      </div>
                    </div>
                  )}
              </div>

              {/* LEYENDA DEL MAPA - DATOS GPS REALES */}
              {selectedViaje && trackingData.length > 0 && (
                <div className="bg-gray-50 border-t p-3">
                  <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                    <Info className="h-4 w-4 mr-1" />
                    Datos de la Ruta GPS
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-xs mb-2">
                    {/* INICIO GPS (Primer punto de trackingData) */}
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-green-500 rounded-full mr-2 border-2 border-white shadow-sm flex items-center justify-center text-white font-bold text-[8px]">
                        I
                      </div>
                      <span className="text-gray-700">
                        Inicio GPS:{" "}
                        {(() => {
                          try {
                            // 1. Intentar obtener la Hora de Llegada Real del primer tramo (Origen)
                            const primerTramo = selectedViaje.tramos?.[0];
                            if (primerTramo?.horaLlegadaReal) {
                              const fecha = parseDateLocal(
                                primerTramo.horaLlegadaReal,
                              );
                              return fecha.toLocaleDateString("es-PE", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            }

                            // 2. Si no hay evento, usar el primer punto GPS como fallback
                            const primerPunto = trackingData[0];
                            if (primerPunto?.creationDate) {
                              const fecha = new Date(primerPunto.creationDate);
                              return fecha.toLocaleDateString("es-PE", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            } else if (primerPunto?.gpstime) {
                              const fecha = new Date(
                                primerPunto.gpstime * 1000,
                              );
                              return fecha.toLocaleDateString("es-PE", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            }

                            return "--"; // Si no hay nada
                          } catch (error) {
                            return "--";
                          }
                        })()}
                      </span>
                    </div>
                    {/* FIN GPS: Prioridad -> HoraSalidaReal (Destino) -> Último punto GPS */}
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-red-500 rounded-full mr-2 border-2 border-white shadow-sm flex items-center justify-center text-white font-bold text-[8px]">
                        F
                      </div>
                      <span className="text-gray-700">
                        Fin GPS:{" "}
                        {(() => {
                          try {
                            const tramos = selectedViaje.tramos;
                            const ultimoTramo = tramos?.[tramos.length - 1];

                            // 1. COMPLETADO: Mostrar hora de salida real del destino
                            if (ultimoTramo?.horaSalidaRealDestino) {
                              const fecha = parseDateLocal(
                                ultimoTramo.horaSalidaRealDestino,
                              );
                              return fecha.toLocaleDateString("es-PE", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            }

                            // 2. EN CURSO: Mostrar ETA si existe
                            if (
                              ultimoTramo?.estado === "en_curso" &&
                              ultimoTramo?.eta
                            ) {
                              return `ETA: ${ultimoTramo.eta}`;
                            }

                            // 3. PENDIENTE: Mostrar hora programada de llegada
                            if (ultimoTramo?.horaSalidaProgramada) {
                              const fecha = parseDateLocal(
                                ultimoTramo.horaSalidaProgramada,
                              );
                              return fecha.toLocaleDateString("es-PE", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            }

                            return "--";
                          } catch (error) {
                            return "--";
                          }
                        })()}
                      </span>
                    </div>
                  </div>

                  {/* VEHÍCULO Y REGISTROS */}
                  <div className="grid grid-cols-2 gap-3 text-xs mb-2">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-blue-500 rounded mr-2 border-2 border-white shadow-sm flex items-center justify-center">
                        <Truck className="h-2 w-2 text-white" />
                      </div>
                      <span className="text-gray-700">
                        Remolcadoar: {selectedViaje.vehiculo?.placa || "N/A"}
                        {selectedViaje.vehiculo?.imei && (
                          <span className="ml-2 text-gray-500">
                            (IMEI: {selectedViaje.vehiculo.imei})
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-700 font-medium">
                        Registros GPS:{" "}
                        {
                          getValidTrackingData(trackingData, selectedViaje)
                            .length
                        }
                      </span>
                    </div>
                  </div>

                  {/* INFORMACIÓN ADICIONAL DE LOS DATOS GPS (CÁLCULOS) */}
                  <div className="grid grid-cols-4 gap-2 text-xs border-t pt-2 mt-2">
                    {/* 1. DURACIÓN (Usando datos válidos filtrados) */}
                    <div>
                      <span className="text-gray-600">Duración:</span>
                      <p className="font-medium text-gray-900">
                        {(() => {
                          try {
                            const datosValidos = getValidTrackingData(
                              trackingData,
                              selectedViaje,
                            );

                            if (datosValidos.length < 2) return "N/A";

                            // Obtener fecha/timestamp de inicio del primer punto VÁLIDO
                            const primerPuntoValido = datosValidos[0];
                            const fechaInicio = primerPuntoValido.creationDate
                              ? new Date(primerPuntoValido.creationDate)
                              : new Date(primerPuntoValido.gpstime * 1000);

                            // Obtener fecha/timestamp del último punto VÁLIDO
                            const ultimoPuntoValido =
                              datosValidos[datosValidos.length - 1];
                            const fechaFin = ultimoPuntoValido.creationDate
                              ? new Date(ultimoPuntoValido.creationDate)
                              : new Date(ultimoPuntoValido.gpstime * 1000);

                            if (
                              isNaN(fechaInicio.getTime()) ||
                              isNaN(fechaFin.getTime())
                            ) {
                              return "N/A";
                            }

                            const duracion =
                              fechaFin.getTime() - fechaInicio.getTime();
                            const horas = Math.floor(
                              duracion / (1000 * 60 * 60),
                            );
                            const minutos = Math.floor(
                              (duracion % (1000 * 60 * 60)) / (1000 * 60),
                            );
                            return `${horas}h ${minutos}m`;
                          } catch (error) {
                            return "N/A";
                          }
                        })()}
                      </p>
                    </div>

                    {/* 2. DISTANCIA (Usando datos válidos filtrados) */}
                    <div>
                      <span className="text-gray-600">Distancia:</span>
                      <p className="font-medium text-gray-900">
                        {(() => {
                          try {
                            const datosValidos = getValidTrackingData(
                              trackingData,
                              selectedViaje,
                            );
                            if (datosValidos.length < 2) return "N/A";

                            const calcularDistancia = (
                              lat1,
                              lng1,
                              lat2,
                              lng2,
                            ) => {
                              const R = 6371;
                              const toRad = (deg) => (deg * Math.PI) / 180;

                              const dLat = toRad(lat2 - lat1);
                              const dLng = toRad(lng2 - lng1);

                              const a =
                                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                                Math.cos(toRad(lat1)) *
                                  Math.cos(toRad(lat2)) *
                                  Math.sin(dLng / 2) *
                                  Math.sin(dLng / 2);

                              const c =
                                2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                              return R * c;
                            };

                            let distanciaTotal = 0;
                            for (let i = 1; i < datosValidos.length; i++) {
                              const punto1 = datosValidos[i - 1];
                              const punto2 = datosValidos[i];

                              const lat1 = parseFloat(punto1.latitude);
                              const lng1 = parseFloat(punto1.longitude);
                              const lat2 = parseFloat(punto2.latitude);
                              const lng2 = parseFloat(punto2.longitude);

                              if (
                                !isNaN(lat1) &&
                                !isNaN(lng1) &&
                                !isNaN(lat2) &&
                                !isNaN(lng2)
                              ) {
                                distanciaTotal += calcularDistancia(
                                  lat1,
                                  lng1,
                                  lat2,
                                  lng2,
                                );
                              }
                            }
                            return `${distanciaTotal.toFixed(1)} km`;
                          } catch (error) {
                            return "N/A";
                          }
                        })()}
                      </p>
                    </div>

                    {/* 3. VELOCIDAD PROMEDIO (Usando datos válidos filtrados) */}
                    <div>
                      <span className="text-gray-600">Velocidad prom:</span>
                      <p className="font-medium text-gray-900">
                        {(() => {
                          try {
                            const datosValidos = getValidTrackingData(
                              trackingData,
                              selectedViaje,
                            );
                            const puntosConVelocidad = datosValidos.filter(
                              (point) =>
                                point.speed !== null &&
                                point.speed !== undefined &&
                                !isNaN(parseFloat(point.speed)),
                            );
                            if (puntosConVelocidad.length === 0) return "N/A";

                            const velocidadPromedio =
                              puntosConVelocidad.reduce(
                                (sum, point) => sum + parseFloat(point.speed),
                                0,
                              ) / puntosConVelocidad.length;

                            return `${velocidadPromedio.toFixed(1)} km/h`;
                          } catch (error) {
                            return "N/A";
                          }
                        })()}
                      </p>
                    </div>

                    {/* 4. KILOMETRAJE (Asumiendo que es el último 'mileage' reportado, filtrado) */}
                    <div>
                      <span className="text-gray-600">Kilometraje:</span>
                      <p className="font-medium text-gray-900">
                        {(() => {
                          try {
                            const datosValidos = getValidTrackingData(
                              trackingData,
                              selectedViaje,
                            );

                            if (datosValidos.length === 0) return "N/A";

                            // En lugar de usar el último del RAW, usamos el último del filtrado.
                            const ultimoPunto =
                              datosValidos[datosValidos.length - 1];

                            if (ultimoPunto?.mileage) {
                              // Manteniendo la lógica de formateo que tenías
                              const km =
                                ultimoPunto.mileage > 10000
                                  ? (ultimoPunto.mileage / 1000).toFixed(0)
                                  : ultimoPunto.mileage.toFixed(0);
                              return `${km} km`;
                            }
                            return "N/A";
                          } catch (error) {
                            return "N/A";
                          }
                        })()}
                      </p>
                    </div>
                  </div>

                  {/* LEYENDA DEL TRAZADO */}
                  <div className="mt-2 flex items-center">
                    <div className="w-8 h-1 bg-blue-500 mr-2 opacity-80"></div>
                    <span className="text-gray-700 text-xs">
                      Ruta GPS recorrida por el vehículo
                    </span>
                  </div>

                  {/* ⚠️ ADVERTENCIA SI NO HAY LLEGADA AL ORIGEN */}
                  {selectedViaje.tramos?.[0] &&
                    !selectedViaje.tramos[0].horaLlegadaReal && (
                      <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="text-xs text-yellow-800 flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          ⚠️ Las métricas GPS se mostrarán una vez que el
                          vehículo llegue al punto de origen
                        </p>
                      </div>
                    )}
                </div>
              )}

              <div className="p-3 bg-gray-50 border-t">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <div
                        className={`w-3 h-3 rounded-full mr-2 ${
                          window.trackingMap ? "bg-green-400" : "bg-yellow-400"
                        }`}
                      ></div>
                      <span>
                        Google Maps: {window.trackingMap ? "OK" : "Cargando..."}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div
                        className={`w-3 h-3 rounded-full mr-2 ${
                          getValidTrackingData(trackingData, selectedViaje)
                            .length > 0
                            ? "bg-green-400"
                            : "bg-gray-400"
                        }`}
                      ></div>
                      <span>
                        Datos GPS válidos:{" "}
                        {
                          getValidTrackingData(trackingData, selectedViaje)
                            .length
                        }
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div
                        className={`w-3 h-3 rounded-full mr-2 ${
                          selectedViaje?.vehiculo?.id
                            ? "bg-green-400"
                            : "bg-red-400"
                        }`}
                      ></div>
                      <span>
                        Vehículo:{" "}
                        {selectedViaje?.vehiculo?.id
                          ? "Seleccionado"
                          : "No disponible"}
                      </span>
                    </div>
                  </div>

                  {selectedViaje && (
                    <button
                      onClick={async () => {
                        if (selectedViaje.id) {
                          console.log(
                            `🔄 Recargando detalle (incluye GPS) para el viaje ${selectedViaje.codigoViaje}`,
                          );
                          // Llama al detalle completo. fetchViajeDetalle se encarga de llamar a loadVehicleTracking
                          await fetchViajeDetalle(selectedViaje.id);
                        }
                      }}
                      // Se deshabilita si está cargando el detalle O el tracking
                      disabled={isLoading || loadingTracking}
                      className="flex items-center px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                    >
                      <RefreshCw
                        // Usamos loadingTracking para el ícono, ya que es el paso final
                        className={`h-4 w-4 mr-1 ${
                          loadingTracking ? "animate-spin" : ""
                        }`}
                      />
                      Actualizar GPS
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {selectedViaje && !isLoading ? (
              <>
                <div className="bg-white rounded-lg shadow-sm p-3">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center text-sm">
                    <Package className="h-4 w-4 mr-2" />
                    Información del Viaje
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600 text-xs">Cliente:</span>
                      <p className="font-medium truncate text-sm">
                        {selectedViaje.empresaCliente?.nombre || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">
                        Transportista:
                      </span>
                      <p className="font-medium truncate text-sm">
                        {selectedViaje.empresaTransportista?.nombre || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">
                        Generador de Carga
                      </span>
                      <p className="font-medium truncate text-sm">
                        {selectedViaje.empresaOperador?.nombre || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">Estado:</span>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(
                          selectedViaje.estado,
                        )}`}
                      >
                        {getEstadoIcon(selectedViaje.estado)}
                        <span className="ml-1">
                          {selectedViaje.estado.replace("_", " ").toUpperCase()}
                        </span>
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">
                        Posicionamiento en Origen:
                      </span>
                      <p className="font-medium text-sm">
                        {formatDate(selectedViaje.fechaInicioProgramada)}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">
                        Posicionamiento en Destino:
                      </span>
                      <p className="font-medium text-sm">
                        {formatDate(selectedViaje.fechaFinProgramada)}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">Remolcador:</span>
                      <p className="font-medium text-sm">
                        {selectedViaje.vehiculo?.placa || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600 text-xs">Progreso:</span>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full transition-all"
                            style={{ width: `${selectedViaje.progreso}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-blue-600">
                          {selectedViaje.progreso}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-3">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center text-sm">
                    <MapPin className="h-4 w-4 mr-2" />
                    Información de Tramos
                  </h3>
                  {selectedViaje &&
                  selectedViaje.tramos &&
                  selectedViaje.tramos.length > 0 ? (
                    <div className="space-y-2">
                      {selectedViaje.tramos.map((tramo, index) => (
                        <div
                          key={index}
                          className="p-2 bg-gray-50 rounded border border-gray-200"
                        >
                          {/* Primera línea: Tramo, Origen-Destino y Estado */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 flex-1">
                              <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                                Tramo {tramo.orden || index + 1}:
                              </span>
                              <span className="text-xs text-gray-900">
                                {tramo.establecimientoOrigen?.nombre ||
                                  "Origen"}{" "}
                                →{" "}
                                {tramo.establecimientoDestino?.nombre ||
                                  "Destino"}
                              </span>
                            </div>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(
                                tramo.estado || "pendiente",
                              )}`}
                            >
                              {getEstadoIcon(tramo.estado || "pendiente")}
                              <span className="ml-1">
                                {(tramo.estado || "pendiente")
                                  .replace("_", " ")
                                  .toUpperCase()}
                              </span>
                            </span>

                            {/* ⭐ NUEVO BOTÓN DE ANÁLISIS */}
                            {(tramo.estado === "completado" ||
                              tramo.estado === "en_curso") && (
                              <button
                                onClick={() => setTramoAnalisis(tramo)}
                                className="flex items-center px-2 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs"
                                title="Ver análisis detallado de demoras"
                              >
                                <FileText className="h-3 w-3 mr-1" />
                                Análisis
                              </button>
                            )}
                          </div>

                          {(() => {
                            const estado = tramo.estado || "pendiente";
                            const tieneETA =
                              tramo.eta !== null &&
                              tramo.eta !== undefined &&
                              tramo.eta !== "";
                            const tieneAvance =
                              tramo.avance !== null &&
                              tramo.avance !== undefined &&
                              tramo.avance > 0;

                            // Calcular progreso real basado en tiempos
                            const calcularProgreso = () => {
                              const ahora = new Date();
                              const llegadaProg = tramo.horaLlegadaProgramada
                                ? parseDateLocal(tramo.horaLlegadaProgramada)
                                : null;
                              const salidaProg = tramo.horaSalidaProgramada
                                ? parseDateLocal(tramo.horaSalidaProgramada)
                                : null;
                              const llegadaReal = tramo.horaLlegadaReal
                                ? parseDateLocal(tramo.horaLlegadaReal)
                                : null;
                              const salidaReal = tramo.horaSalidaReal
                                ? parseDateLocal(tramo.horaSalidaReal)
                                : null;

                              // COMPLETADO: 100%
                              if (estado === "completado") {
                                return 100;
                              }
                              const config =
                                SEMAFORO_CONFIG[tramo.semaforo] ||
                                SEMAFORO_CONFIG.GRIS;
                              // EN CURSO: Calcular progreso entre llegada real y salida programada
                              if (estado === "en_curso") {
                                // Si ya llegó pero aún no sale
                                if (llegadaReal && salidaProg) {
                                  const tiempoTotal =
                                    salidaProg.getTime() -
                                    llegadaReal.getTime();
                                  const tiempoTranscurrido =
                                    ahora.getTime() - llegadaReal.getTime();

                                  if (tiempoTotal <= 0) return 95; // Si el tiempo total es 0 o negativo, está cerca de terminar

                                  const progreso =
                                    (tiempoTranscurrido / tiempoTotal) * 100;
                                  return Math.min(
                                    95,
                                    Math.max(5, Math.round(progreso)),
                                  ); // Entre 5% y 95%
                                }
                                // Si no hay datos suficientes, mostrar un progreso medio
                                return 50;
                              }

                              // PENDIENTE/RETRASADO: Calcular progreso entre llegada programada y salida programada
                              if (
                                (estado === "pendiente" ||
                                  estado === "retrasado") &&
                                llegadaProg &&
                                salidaProg
                              ) {
                                // Si aún no ha llegado la hora de inicio
                                if (ahora < llegadaProg) return 0;

                                const tiempoTotal =
                                  salidaProg.getTime() - llegadaProg.getTime();
                                const tiempoTranscurrido =
                                  ahora.getTime() - llegadaProg.getTime();

                                if (tiempoTotal <= 0) return 0;

                                const progreso =
                                  (tiempoTranscurrido / tiempoTotal) * 100;
                                return Math.min(
                                  100,
                                  Math.max(0, Math.round(progreso)),
                                );
                              }

                              return 0;
                            };

                            const progresoCalculado = calcularProgreso();

                            // ✅ COMPLETADO: Mostrar todas las horas reales y métricas
                            if (estado === "completado") {
                              return (
                                <div className="space-y-2 mt-2 ml-1">
                                  {/* Horas reales ORIGEN */}
                                  <div className="bg-blue-50 p-2 rounded border border-blue-200">
                                    <div className="text-xs font-semibold text-blue-900 mb-1">
                                      📍 Establecimiento Origen:{" "}
                                      {tramo.establecimientoOrigen?.nombre ||
                                        "N/A"}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      {tramo.horaLlegadaReal && (
                                        <div className="flex items-center text-xs text-green-700">
                                          <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                                          <span className="font-medium">
                                            Llegada:{" "}
                                            {formatDate(tramo.horaLlegadaReal)}{" "}
                                            {formatTime(tramo.horaLlegadaReal)}
                                          </span>
                                        </div>
                                      )}
                                      {tramo.horaSalidaReal && (
                                        <div className="flex items-center text-xs text-green-700">
                                          <Play className="h-3 w-3 mr-1 text-green-500" />
                                          <span className="font-medium">
                                            Salida:{" "}
                                            {formatDate(tramo.horaSalidaReal)}{" "}
                                            {formatTime(tramo.horaSalidaReal)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {console.log("Tramo Completado:", tramo)}
                                  {/* Horas reales DESTINO */}
                                  <div className="bg-green-50 p-2 rounded border border-green-200">
                                    <div className="text-xs font-semibold text-green-900 mb-1">
                                      🎯 Establecimiento Destino2:{" "}
                                      {tramo.establecimientoDestino?.nombre ||
                                        "N/A"}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      {tramo.horaLlegadaRealDestino ? (
                                        <div className="flex items-center text-xs text-green-700">
                                          <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                                          <span className="font-medium">
                                            Llegada:{" "}
                                            {formatDate(
                                              tramo.horaLlegadaRealDestino,
                                            )}{" "}
                                            {formatTime(
                                              tramo.horaLlegadaRealDestino,
                                            )}
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center text-xs text-gray-500">
                                          <Clock className="h-3 w-3 mr-1" />
                                          <span className="italic">
                                            Sin registro de llegada
                                          </span>
                                        </div>
                                      )}
                                      {tramo.horaSalidaRealDestino ? (
                                        <div className="flex items-center text-xs text-green-700">
                                          <Play className="h-3 w-3 mr-1 text-green-500" />
                                          <span className="font-medium">
                                            Salida:{" "}
                                            {formatDate(
                                              tramo.horaSalidaRealDestino,
                                            )}{" "}
                                            {formatTime(
                                              tramo.horaSalidaRealDestino,
                                            )}
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center text-xs text-gray-500">
                                          <Play className="h-3 w-3 mr-1" />
                                          <span className="italic">
                                            Sin registro de salida
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Métricas de rendimiento del ORIGEN (Cita 1) */}
                                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-200">
                                    <div className="flex items-center mb-2">
                                      <Target className="h-4 w-4 text-blue-600 mr-2" />
                                      <h4 className="text-xs font-bold text-blue-900">
                                        Métricas de Rendimiento -
                                        Establecimiento Origen
                                      </h4>
                                    </div>

                                    <div className="grid grid-cols-3 gap-3">
                                      {/* Tardanza */}
                                      <div className="bg-white p-2 rounded-md shadow-sm">
                                        <div className="flex items-center mb-1">
                                          <Clock className="h-3 w-3 text-gray-500 mr-1" />
                                          <span className="text-xs text-gray-600 font-medium">
                                            Tardanza
                                          </span>
                                        </div>
                                        {tramo.tardanzaCita1 !== null &&
                                        tramo.tardanzaCita1 !== undefined ? (
                                          <div className="flex items-baseline">
                                            <p
                                              className={`text-lg font-bold ${
                                                tramo.tardanzaCita1 === 0
                                                  ? "text-green-600"
                                                  : tramo.tardanzaCita1 <= 15
                                                    ? "text-yellow-600"
                                                    : "text-red-600"
                                              }`}
                                            >
                                              {tramo.tardanzaCita1}
                                            </p>
                                            <span className="text-xs text-gray-500 ml-1">
                                              min
                                            </span>
                                          </div>
                                        ) : (
                                          <p className="text-xs text-gray-400 italic">
                                            No calculado
                                          </p>
                                        )}
                                        {tramo.tardanzaCita1 === 0 && (
                                          <p className="text-xs text-green-600 mt-1">
                                            ✓ A tiempo
                                          </p>
                                        )}
                                      </div>

                                      {/* Permanencia */}
                                      <div className="bg-white p-2 rounded-md shadow-sm">
                                        <div className="flex items-center mb-1">
                                          <AlertCircle className="h-3 w-3 text-gray-500 mr-1" />
                                          <span className="text-xs text-gray-600 font-medium">
                                            Permanencia
                                          </span>
                                        </div>
                                        {tramo.tiempoPermanenciaCita1 !==
                                          null &&
                                        tramo.tiempoPermanenciaCita1 !==
                                          undefined ? (
                                          <div className="flex items-baseline">
                                            <p className="text-lg font-bold text-blue-600">
                                              {tramo.tiempoPermanenciaCita1}
                                            </p>
                                            <span className="text-xs text-gray-500 ml-1">
                                              min
                                            </span>
                                          </div>
                                        ) : (
                                          <p className="text-xs text-gray-400 italic">
                                            No calculado
                                          </p>
                                        )}
                                        <p className="text-xs text-gray-500 mt-1">
                                          Tiempo total en sitio
                                        </p>
                                      </div>

                                      {/* Atención */}
                                      <div className="bg-white p-2 rounded-md shadow-sm">
                                        <div className="flex items-center mb-1">
                                          <CheckCircle className="h-3 w-3 text-gray-500 mr-1" />
                                          <span className="text-xs text-gray-600 font-medium">
                                            Tiempo Atención
                                          </span>
                                        </div>
                                        {tramo.tiempoAtencionCita1 !== null &&
                                        tramo.tiempoAtencionCita1 !==
                                          undefined ? (
                                          <div className="flex items-baseline">
                                            <p className="text-lg font-bold text-purple-600">
                                              {tramo.tiempoAtencionCita1}
                                            </p>
                                            <span className="text-xs text-gray-500 ml-1">
                                              min
                                            </span>
                                          </div>
                                        ) : (
                                          <p className="text-xs text-gray-400 italic">
                                            No calculado
                                          </p>
                                        )}
                                        <p className="text-xs text-gray-500 mt-1">
                                          Diff. vs programado
                                        </p>
                                      </div>
                                    </div>

                                    {/* Resumen visual */}
                                    {tramo.tardanzaCita1 !== null && (
                                      <div className="mt-3 pt-2 border-t border-blue-200">
                                        <div className="flex items-center justify-between text-xs">
                                          <span className="text-gray-600">
                                            Evaluación General:
                                          </span>
                                          <span
                                            className={`font-bold ${
                                              tramo.tardanzaCita1 === 0
                                                ? "text-green-600"
                                                : tramo.tardanzaCita1 <= 15
                                                  ? "text-yellow-600"
                                                  : "text-red-600"
                                            }`}
                                          >
                                            {tramo.tardanzaCita1 === 0
                                              ? "✓ EXCELENTE"
                                              : tramo.tardanzaCita1 <= 15
                                                ? "⚠ ACEPTABLE"
                                                : "✗ FUERA DE SLA"}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {/* ⭐ AGREGAR ESTE BLOQUE DESPUÉS DE LAS MÉTRICAS DEL ORIGEN */}
                                  {/* Métricas de rendimiento del DESTINO (Cita 2) */}
                                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200">
                                    <div className="flex items-center mb-2">
                                      <Target className="h-4 w-4 text-green-600 mr-2" />
                                      <h4 className="text-xs font-bold text-green-900">
                                        Métricas de Rendimiento -
                                        Establecimiento Destino
                                      </h4>
                                    </div>

                                    <div className="grid grid-cols-3 gap-3">
                                      {/* Tardanza */}
                                      <div className="bg-white p-2 rounded-md shadow-sm">
                                        <div className="flex items-center mb-1">
                                          <Clock className="h-3 w-3 text-gray-500 mr-1" />
                                          <span className="text-xs text-gray-600 font-medium">
                                            Tardanza
                                          </span>
                                        </div>
                                        {tramo.tardanzaCita2 !== null &&
                                        tramo.tardanzaCita2 !== undefined ? (
                                          <div className="flex items-baseline">
                                            <p
                                              className={`text-lg font-bold ${
                                                tramo.tardanzaCita2 === 0
                                                  ? "text-green-600"
                                                  : tramo.tardanzaCita2 <= 15
                                                    ? "text-yellow-600"
                                                    : "text-red-600"
                                              }`}
                                            >
                                              {tramo.tardanzaCita2}
                                            </p>
                                            <span className="text-xs text-gray-500 ml-1">
                                              min
                                            </span>
                                          </div>
                                        ) : (
                                          <p className="text-xs text-gray-400 italic">
                                            No calculado
                                          </p>
                                        )}
                                        {tramo.tardanzaCita2 === 0 && (
                                          <p className="text-xs text-green-600 mt-1">
                                            ✓ A tiempo
                                          </p>
                                        )}
                                      </div>

                                      {/* Permanencia */}
                                      <div className="bg-white p-2 rounded-md shadow-sm">
                                        <div className="flex items-center mb-1">
                                          <AlertCircle className="h-3 w-3 text-gray-500 mr-1" />
                                          <span className="text-xs text-gray-600 font-medium">
                                            Permanencia
                                          </span>
                                        </div>
                                        {tramo.tiempoPermanenciaCita2 !==
                                          null &&
                                        tramo.tiempoPermanenciaCita2 !==
                                          undefined ? (
                                          <div className="flex items-baseline">
                                            <p className="text-lg font-bold text-blue-600">
                                              {tramo.tiempoPermanenciaCita2}
                                            </p>
                                            <span className="text-xs text-gray-500 ml-1">
                                              min
                                            </span>
                                          </div>
                                        ) : (
                                          <p className="text-xs text-gray-400 italic">
                                            No calculado
                                          </p>
                                        )}
                                        <p className="text-xs text-gray-500 mt-1">
                                          {tramo.tiempoPermanenciaCita2
                                            ? `${(
                                                tramo.tiempoPermanenciaCita2 /
                                                60
                                              ).toFixed(1)} horas`
                                            : "Tiempo total en sitio"}
                                        </p>
                                      </div>

                                      {/* Atención */}
                                      <div className="bg-white p-2 rounded-md shadow-sm">
                                        <div className="flex items-center mb-1">
                                          <CheckCircle className="h-3 w-3 text-gray-500 mr-1" />
                                          <span className="text-xs text-gray-600 font-medium">
                                            Tiempo Atención
                                          </span>
                                        </div>
                                        {tramo.tiempoAtencionCita2 !== null &&
                                        tramo.tiempoAtencionCita2 !==
                                          undefined ? (
                                          <div className="flex items-baseline">
                                            <p
                                              className={`text-lg font-bold ${
                                                tramo.slaMinutos &&
                                                tramo.tiempoPermanenciaCita2 <=
                                                  tramo.slaMinutos
                                                  ? "text-green-600"
                                                  : "text-red-600"
                                              }`}
                                            >
                                              {tramo.tiempoAtencionCita2}
                                            </p>
                                            <span className="text-xs text-gray-500 ml-1">
                                              min
                                            </span>
                                          </div>
                                        ) : (
                                          <p className="text-xs text-gray-400 italic">
                                            No calculado
                                          </p>
                                        )}
                                        <p className="text-xs text-gray-500 mt-1">
                                          vs SLA: {tramo.slaMinutos || "N/A"}{" "}
                                          min
                                        </p>
                                      </div>
                                    </div>

                                    {/* Resumen visual */}
                                    {tramo.tardanzaCita2 !== null && (
                                      <div className="mt-3 pt-2 border-t border-green-200">
                                        <div className="flex items-center justify-between text-xs">
                                          <span className="text-gray-600">
                                            Evaluación General:
                                          </span>
                                          <span
                                            className={`font-bold ${
                                              tramo.slaMinutos &&
                                              tramo.tiempoPermanenciaCita2 <=
                                                tramo.slaMinutos
                                                ? "text-green-600"
                                                : "text-red-600"
                                            }`}
                                          >
                                            {tramo.slaMinutos &&
                                            tramo.tiempoPermanenciaCita2 <=
                                              tramo.slaMinutos
                                              ? "✓ DENTRO DE SLA"
                                              : "✗ EXCEDIÓ SLA"}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {/* Barra de progreso completo */}
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-gray-600">
                                        Estado:
                                      </span>
                                      <span className="font-bold text-green-600">
                                        ✓ Completado
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div
                                        className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all"
                                        style={{ width: "100%" }}
                                      />
                                    </div>
                                  </div>

                                  {tramo.observaciones && (
                                    <div className="text-xs text-gray-600 italic pt-1 border-t border-gray-200">
                                      📋 {tramo.observaciones}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            const config =
                              SEMAFORO_CONFIG[tramo.semaforo] ||
                              SEMAFORO_CONFIG.GRIS;
                            // ✅ EN CURSO: Mostrar progreso dinámico, ETA y métricas en tiempo real
                            if (estado === "en_curso") {
                              console.log(
                                "🔍 DEBUG EN_CURSO - Tramo:",
                                tramo.id,
                              );
                              console.log("📍 ETA:", tramo.eta);
                              console.log("📊 Avance:", tramo.avance);

                              const tieneETA =
                                tramo.eta !== null &&
                                tramo.eta !== undefined &&
                                tramo.eta !== "";
                              const tieneAvance =
                                tramo.avance !== null &&
                                tramo.avance !== undefined;
                              // tramo.avance !== "0.0";

                              // El avance ya viene como porcentaje (0-100) desde el backend
                              const progresoCalculado = tieneAvance
                                ? Math.min(
                                    100,
                                    Math.max(0, parseFloat(tramo.avance)),
                                  )
                                : 0;

                              console.log(
                                "✅ tieneETA:",
                                tieneETA,
                                "| tieneAvance:",
                                tieneAvance,
                                "| progreso:",
                                progresoCalculado,
                              );

                              return (
                                <div className="space-y-2 mt-2 ml-1">
                                  {/* Horas reales ORIGEN */}
                                  <div className="bg-blue-50 p-2 rounded border border-blue-200">
                                    <div className="text-xs font-semibold text-blue-900 mb-1">
                                      📍 Establecimiento Origen:{" "}
                                      {tramo.establecimientoOrigen?.nombre ||
                                        "N/A"}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      {tramo.horaLlegadaReal ? (
                                        <div className="flex items-center text-xs text-green-700">
                                          <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                                          <span className="font-medium">
                                            Llegada:{" "}
                                            {formatDate(tramo.horaLlegadaReal)}{" "}
                                            {formatTime(tramo.horaLlegadaReal)}
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center text-xs text-gray-500">
                                          <Clock className="h-3 w-3 mr-1" />
                                          <span className="italic">
                                            Sin registro
                                          </span>
                                        </div>
                                      )}
                                      {tramo.horaSalidaReal ? (
                                        <div className="flex items-center text-xs text-green-700">
                                          <Play className="h-3 w-3 mr-1 text-green-500" />
                                          <span className="font-medium">
                                            Salida:{" "}
                                            {formatDate(tramo.horaSalidaReal)}{" "}
                                            {formatTime(tramo.horaSalidaReal)}
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center text-xs text-gray-500">
                                          <Play className="h-3 w-3 mr-1" />
                                          <span className="italic">
                                            Sin registro
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Horas DESTINO */}
                                  <div className="bg-orange-50 p-2 rounded border border-orange-200">
                                    <div className="text-xs font-semibold text-orange-900 mb-1">
                                      🎯 Establecimiento Destino:{" "}
                                      {tramo.establecimientoDestino?.nombre ||
                                        "N/A"}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      {tramo.horaLlegadaRealDestino ? (
                                        <div className="flex items-center text-xs text-green-700">
                                          <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                                          <span className="font-medium">
                                            Llegada:{" "}
                                            {formatDate(
                                              tramo.horaLlegadaRealDestino,
                                            )}{" "}
                                            {formatTime(
                                              tramo.horaLlegadaRealDestino,
                                            )}
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center text-xs text-orange-600">
                                          <Clock className="h-3 w-3 mr-1 animate-pulse" />
                                          <span className="font-medium italic">
                                            🚚 En ruta...
                                          </span>
                                        </div>
                                      )}
                                      {tramo.horaSalidaProgramada && (
                                        <div className="flex items-center text-xs text-orange-600">
                                          <Play className="h-3 w-3 mr-1" />
                                          <span className="font-medium">
                                            Llegada prog:{" "}
                                            {formatDate(
                                              tramo.horaSalidaProgramada,
                                            )}{" "}
                                            {formatTime(
                                              tramo.horaSalidaProgramada,
                                            )}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* ⭐ BLOQUE DE ETA Y AVANCE GPS */}
                                  {tieneETA || tieneAvance ? (
                                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-3 rounded-lg border-2 border-orange-300 shadow-sm">
                                      <div className="grid grid-cols-2 gap-4">
                                        {/* ETA */}
                                        <div className="bg-white p-2 rounded-md border border-orange-200">
                                          <span className="text-orange-700 text-xs font-semibold flex items-center mb-1">
                                            <Clock className="h-4 w-4 mr-1" />
                                            ETA Calculado:
                                          </span>
                                          <p className="font-bold text-orange-900 text-2xl">
                                            {tramo.eta || "Calculando..."}
                                          </p>
                                        </div>

                                        {/* Avance GPS */}
                                        <div className="bg-white p-2 rounded-md border border-orange-200">
                                          <span className="text-orange-700 text-xs font-semibold flex items-center mb-1">
                                            <Navigation className="h-4 w-4 mr-1" />
                                            Avance GPS:
                                          </span>
                                          <p className="font-bold text-orange-900 text-2xl">
                                            {progresoCalculado.toFixed(1)}%
                                          </p>
                                        </div>
                                      </div>

                                      {/* Barra de progreso visual */}
                                      <div className="mt-3">
                                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                          <div
                                            className="bg-gradient-to-r from-orange-500 to-amber-600 h-3 rounded-full transition-all duration-500 ease-out"
                                            style={{
                                              width: `${progresoCalculado}%`,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="bg-yellow-50 p-2 rounded border border-yellow-200">
                                      <p className="text-xs text-yellow-800 flex items-center">
                                        <AlertCircle className="h-3 w-3 mr-1" />
                                        ⚠️ No hay datos de GPS disponibles para
                                        calcular ETA y Avance
                                      </p>
                                    </div>
                                  )}

                                  {/* Barra de progreso del tramo */}
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-gray-600">
                                        Progreso del Tramo:
                                      </span>
                                      <span className="font-medium text-blue-600">
                                        {progresoCalculado.toFixed(1)}%
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div
                                        className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all"
                                        style={{
                                          width: `${progresoCalculado}%`,
                                        }}
                                      />
                                    </div>
                                  </div>

                                  {/* Observaciones */}
                                  {tramo.observaciones && (
                                    <div className="text-xs text-gray-600 italic pt-1 border-t border-gray-200">
                                      📋 {tramo.observaciones}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            // ✅ PENDIENTE/RETRASADO: Mostrar horas programadas y progreso basado en tiempo
                            if (
                              estado === "pendiente" ||
                              estado === "retrasado"
                            ) {
                              return (
                                <div className="space-y-2 mt-2 ml-1">
                                  {/* Horas programadas */}
                                  <div className="grid grid-cols-2 gap-2">
                                    {tramo.horaLlegadaProgramada && (
                                      <div className="flex items-center text-xs text-gray-600">
                                        <Clock className="h-3 w-3 mr-1 text-gray-400" />
                                        <span className="font-medium">
                                          Llegada:{" "}
                                          {formatDate(
                                            tramo.horaLlegadaProgramada,
                                          )}{" "}
                                          {formatTime(
                                            tramo.horaLlegadaProgramada,
                                          )}
                                        </span>
                                      </div>
                                    )}
                                    {tramo.horaSalidaProgramada && (
                                      <div className="flex items-center text-xs text-gray-600">
                                        <Play className="h-3 w-3 mr-1 text-gray-400" />
                                        <span className="font-medium">
                                          Salida:{" "}
                                          {formatDate(
                                            tramo.horaSalidaProgramada,
                                          )}{" "}
                                          {formatTime(
                                            tramo.horaSalidaProgramada,
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* SLA */}
                                  {tramo.slaMinutos && (
                                    <div className="text-xs text-gray-600">
                                      <span>SLA establecido:</span>
                                      <span className="ml-2 font-medium text-blue-600">
                                        {tramo.slaMinutos} minutos
                                      </span>
                                    </div>
                                  )}

                                  {/* Barra de progreso basada en tiempo */}
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-gray-600">
                                        Progreso:
                                      </span>
                                      <span
                                        className={`font-medium ${
                                          estado === "retrasado"
                                            ? "text-red-600"
                                            : "text-gray-500"
                                        }`}
                                      >
                                        {estado === "retrasado"
                                          ? `⚠️ Retrasado ${progresoCalculado}%`
                                          : `${progresoCalculado}%`}
                                      </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div
                                        className={`h-2 rounded-full transition-all ${
                                          estado === "retrasado"
                                            ? "bg-gradient-to-r from-red-500 to-orange-600"
                                            : "bg-gray-300"
                                        }`}
                                        style={{
                                          width: `${progresoCalculado}%`,
                                        }}
                                      />
                                    </div>
                                  </div>

                                  {tramo.observaciones && (
                                    <div className="text-xs text-gray-600 italic pt-1 border-t border-gray-200">
                                      📋 {tramo.observaciones}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            return null;
                          })()}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        No hay tramos definidos para este viaje
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Los tramos son independientes de los datos GPS del
                        vehículo
                      </p>
                    </div>
                  )}
                  {loadingTracking && (
                    <div className="flex items-center justify-center py-4 mt-3 border-t">
                      <RefreshCw className="h-5 w-5 animate-spin text-blue-500 mr-2" />
                      <span className="text-blue-600 text-sm">
                        Cargando ubicaciones GPS...
                      </span>
                    </div>
                  )}
                  {/* VELOCÍMETRO DE VIAJES */}
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 text-center">
                      Estado General de Viajes
                    </h4>
                    <VelocimetroViajes
                      completados={viajesCompletados}
                      total={totalViajes}
                    />
                  </div>
                </div>

                {/* Panel Demo GPS */}
                {selectedViaje && (
                  <div className="bg-white rounded-lg shadow-sm p-3 border-l-4 border-indigo-500">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 text-sm">🛰 Demo GPS</h3>
                    </div>

                    {simDemoActivo && simDemoViajeIdRef.current === selectedViaje?.id ? (
                      /* Panel demo activo */
                      <div className="space-y-2">
                        <div className="bg-green-50 border border-green-300 rounded p-2 text-xs text-green-800">
                          <div className="font-medium animate-pulse">● Demo corriendo en servidor</div>
                          <div className="text-green-600">1 track cada {simDemoIntervalo}s — polling activo</div>
                        </div>
                        <button
                          onClick={simularAlertasGPS}
                          disabled={simAlertasLoading}
                          className="w-full px-3 py-2 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 disabled:opacity-50">
                          {simAlertasLoading ? "Enviando..." : "🔔 Simular Alertas GPS"}
                        </button>
                        <button onClick={stopDemoSim}
                          className="w-full px-3 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600">
                          ⏹ Detener Demo
                        </button>
                      </div>
                    ) : (
                      /* Panel para iniciar demo */
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500">Inserta 1 track real cada N segundos. El servidor corre solo, sobrevive refresco.</p>
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-gray-500">Intervalo:</span>
                          {[{v:7,l:"7s ·4x"},{v:15,l:"15s ·2x"},{v:30,l:"30s real"}].map(({v,l}) => (
                            <button key={v} onClick={() => setSimDemoIntervalo(v)}
                              className={`px-2 py-0.5 rounded ${
                                simDemoIntervalo === v ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700"}`}>
                              {l}
                            </button>
                          ))}
                        </div>
                        <button
                          onClick={initDemo}
                          disabled={simLoading}
                          className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm">
                          {simLoading ? "Preparando..." : "▶ Iniciar Demo"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : isLoading ? (
              <DetalleViajeSkeleton />
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Selecciona un viaje
                </h3>
                <p className="text-gray-600 text-sm">
                  Elige un viaje para ver el seguimiento GPS con Google Maps en
                  tiempo real.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {tramoAnalisis && (
        <AnalisisDemorasModal
          tramo={tramoAnalisis}
          onClose={() => setTramoAnalisis(null)}
        />
      )}
    </div>
  );
};

export default SeguimientoViajes;
